// src/components/block/liveStreamManagement/useLiveStreamSetup.ts
// v2.33 — deckSlotLayouts
// v2.34 — updateVideoSources accepte extraSources
// v2.39 — Migration previews vers registry.borrow() + stockage des handles
//          Les previews empruntent les sources au lieu de faire getUserMedia direct.
// v2.40 — Auto-fill corrigé : le partage d'écran (type 'screen') n'est JAMAIS
//          ajouté automatiquement aux slots vides. L'utilisateur l'assigne manuellement.
//          buildDefaultVideoRow (dans Deck) : caméras + HLS Video uniquement.
// v2.41 — resetDeckInit : réinitialise deckInitDone + deckPreviewInitRef + lastSourceIdsRef
//          pour que le deck reparte de zéro au redémarrage d'une chaîne.
// v2.42 — updateVideoSources : préserve les sources URL customs (HLS ajoutés manuellement)
//          qui ne font partie d'aucune liste reconstruite.
// v2.43 — Auto-fill post-init : uniquement pour les sources jamais assignées à un slot.
//          Les sources retirées manuellement ne sont plus réaffectées automatiquement.
// v2.44 — poweredOffSlots : l'auto-borrow des mini-vues ignore les sources éteintes.
//          previewBorrowHandlesRef exposé dans le return pour nettoyage externe (power OFF).
// v2.45 — handleSignalingMessageRef : permet à LiveStreamManager de surcharger le handler
//          de signalisation pour passer les callbacks intercomm (onDeviceDisconnected etc.)
// v2.46 — Suppression/déconnexion d'un device : le device est coupé de force (force_disconnect)
//          et toute trace locale est effacée (session WHEP, buffers, source `remote-<id>`,
//          intercomm) via remoteDeviceTeardownRef branché par LiveStreamManager.
// v2.47 — updateVideoSources : bail-out d'égalité (sameVideoSourceList) — renvoie l'état
//          précédent quand la liste reconstruite est identique, au lieu d'un tableau neuf
//          à chaque appel. Coupe la cascade de rendus déclenchée par remoteConnections
//          pendant la phase de connexion WebRTC. Updater rendu non mutant au passage.

import { useState, useEffect, useRef, useCallback } from 'react';
import { afficherLimite } from '../../../dataTypes/adminManagment/quota';
import { BUY_DEVICES_LABEL, openBuyDevices } from '../../../utils/utils_devicePurchase';
import type React from 'react';

import { useMediaStream, VideoSource, RemoteConnection, SLOT_COUNT, LayoutSlot } from '../../../contexts/MediaStreamContext';
import { useProductionControlRoom } from '../../../contexts/ProductionControlRoomContext';

import { DeckKeyMap } from '../../../dataTypes/liveStreamManagment/types_deck';
import { loadKeyMap, saveKeyMap, bindingMatches } from '../../../dataTypes/liveStreamManagment/utils_deck';
import {
  SignalingRefs, SignalingCallbacks, SignalingConfig, RegieControlState, RegieControlRequest,
  decrireRegie,
} from '../../../dataTypes/liveStreamManagment/types_signaling';
import { RemoteDevice, RemoteDeviceInfo, DeviceAuthMode } from '../../../dataTypes/liveStreamManagment/types_webrtc';
import { DeviceGeoReport } from '../../../dataTypes/liveStreamManagment/types_media';

import {
  fetchRemoteDevices, registerRemoteDevice,
  deleteRemoteDevice, regenerateDevicePassword, updateRemoteDevice,
  RegisteredRemoteDevice,
} from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import {
  incrementAdminDevicesUsed, decrementAdminDevicesUsed, createAdminActionLog,
} from '../../../backendServices/adminManagment/api';
import { useTypedSelector, useTypedDispatch } from '../../../store/useTypeSelector';
import { setAdmin } from '../../../store/adminAuthenticationManagment/actions';
import {
  connectToSignalingServer as apiConnectToSignalingServer,
  disconnectSignaling      as apiDisconnectSignaling,
} from '../../../backendServices/liveStreamManagment/api_signalingWs';
import {
  cleanupDeviceBuffers  as apiCleanupDeviceBuffers,
} from '../../../backendServices/liveStreamManagment/api_webrtcBuffers';
import {
  connectToDevice          as apiConnectToDevice,
  handleRemoteAnswer       as apiHandleRemoteAnswer,
  abandonDeviceNegotiation,
  resumeDeviceNegotiation,
} from '../../../backendServices/liveStreamManagment/api_webrtcPeerConnection';
import { acquireExternalUrlStream as apiAcquireExternalUrlStream } from '../../../backendServices/liveStreamManagment/api_externalSources';
import { probeWhipCodec as apiProbeWhipCodec } from '../../../backendServices/liveStreamManagment/api_whip';
import {
  connectToDeviceWhep as apiConnectToDeviceWhep,
  closeWhepConnection,
  clearDeviceSfuInfo,
  getDeviceSfuInfo,
  isSfuUnavailable,
} from '../../../backendServices/liveStreamManagment/api_whep';
import { buildNegotiatedCodec } from '../../../contexts/MediaStreamContext';
import { forceSdpCodecs } from '../../../dataTypes/liveStreamManagment/utils_sdp';
import { getActiveWhipEncoding, whipProfilePrefix } from '../../../dataTypes/liveStreamManagment/whipEncodingSettings';
import { buildVideoRow, buildDefaultVideoRow } from './ProductionControlRoom_Deck';
import {
  DeckEntries, DECK_RESTORED_EVENT, DeckRestoredDetail, loadDeckEntries, saveDeckEntries, markDeckAuto, deckEntriesKey, loadKnownSources, saveKnownSources, clearKnownSources,
  mergeDeckEntries, applyDeckEntries,
} from '../../../dataTypes/liveStreamManagment/deck_persistence';
import { readChannelSnapshot } from '../../../contexts/MediaStreamContext';
import { loadAudioOnlySources, saveAudioOnlySources } from '../../../dataTypes/liveStreamManagment/utils_audioOnlySource';
import { scheduleAutosave } from '../../../backendServices/liveStreamManagment/regie_autosave';

import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { BorrowHandle, PERSISTENT_SOURCE_TYPES } from '../../../dataTypes/liveStreamManagment/sourceRegistry';

import { sendReturnStreamToDevice, retryPendingReturnStreams, hasPendingReturnStreams } from '../../../backendServices/liveStreamManagment/api_webrtcPeerConnection';
import {
  requestRegieControl, forceRegieControl,
  answerRegieControlRequest, cancelRegieControlRequest,
} from '../../../backendServices/liveStreamManagment/api_signalingWs';

// Cadence de reprise des flux retour restés en attente du canvas de la régie.
// Une seconde : assez court pour que l'opérateur ne voie pas le retour manquer
// au démarrage d'une chaîne, assez long pour qu'un tick à vide ne pèse rien.
const RETURN_RETRY_INTERVAL_MS = 1000;

/**
 * Combien de temps la régie tient la place d'un appareil tombé.
 *
 * Dix minutes. Les deux minutes d'origine étaient calées sur un téléphone qu'on
 * déverrouille tout de suite — or le relevé du 2026-08-28 (Kleon.TV, 32 coupures
 * sur 5 appareils) donne des silences de 3 à 38 minutes dès que la page passe en
 * arrière-plan : poche, écran verrouillé, appel entrant. À 120 s l'emplacement
 * était rendu à tous les coups, et l'opérateur devait rappeler la caméra par
 * l'engrenage — le défaut constaté le 2026-08-31.
 *
 * Au-delà de dix minutes, une vignette « EN ATTENTE » ne renseigne plus personne :
 * la place est rendue et la caméra, à son retour, se reloge où il y a de la place.
 */
const AWAITING_GRACE_MS = 600_000;


// ── Comparaison de sources vidéo ──────────────────────────────────────────────
// v2.47 — `updateVideoSources` reconstruit intégralement la liste à chaque appel,
// et il est appelé sur chaque changement de `remoteConnections` — donc plusieurs
// fois par seconde pendant une négociation WebRTC. Comme les objets étaient
// toujours neufs, `videoSources` changeait d'identité même quand rien n'avait
// bougé, ce qui relançait la cascade deck/multiview (recalcul de `deckSlots`,
// remplacement de tous les slots puisque `fresh !== slot` était toujours vrai,
// puis les effets qui en dépendent). Ces deux helpers permettent de renvoyer
// l'état précédent tel quel quand la liste est identique au champ près.

function sameScreenConfig(
  a: VideoSource['screenConfig'],
  b: VideoSource['screenConfig'],
): boolean {
  if (a === b)      return true;
  if (!a || !b)     return false;
  return a.width     === b.width
      && a.height    === b.height
      && a.frameRate === b.frameRate
      && a.cursor    === b.cursor
      && a.audio     === b.audio;
}

function sameVideoSource(a: VideoSource, b: VideoSource): boolean {
  if (a === b) return true;
  return a.id           === b.id
      && a.label        === b.label
      && a.type         === b.type
      && a.deviceId     === b.deviceId
      && a.externalUrl  === b.externalUrl
      // MediaStream : comparaison par référence, c'est bien l'identité qui compte.
      && a.stream       === b.stream
      && a.screenActive === b.screenActive
      && a.audioVisual  === b.audioVisual
      // `xtream` n'est jamais reconstruit ici : porté par les sources customs,
      // reprises telles quelles depuis l'état précédent.
      && a.xtream       === b.xtream
      && sameScreenConfig(a.screenConfig, b.screenConfig);
}

function sameVideoSourceList(a: VideoSource[], b: VideoSource[]): boolean {
  if (a === b)               return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!sameVideoSource(a[i], b[i])) return false;
  }
  return true;
}

function buildSignalingUrl(serverUrl: string, streamPath: string, silent = false): string {
  const wsBase = serverUrl
    .replace(/\/$/, '')
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://');
  // Le libellé sert uniquement à dire à l'opérateur d'en face QUI détient la main
  // (« Contrôle pris par Chrome — 192.168.4.x »). Rien de sensible n'y transite :
  // le serveur attribue lui-même l'identité de session, il ne fait pas confiance
  // à ce que le client raconte.
  const label = encodeURIComponent(buildRegieLabel());
  // `observer=silent` : superadmin chez un autre créateur. Le serveur ne lui donne
  // jamais la main et ne l'annonce à personne — informatif, il ne confère aucun droit.
  return `${wsBase}/${streamPath}/webrtc/signaling/control?label=${label}${silent ? '&observer=silent' : ''}`;
}

/** Nom lisible de ce poste, pour les messages de bascule de contrôle. */
function buildRegieLabel(): string {
  const ua = navigator.userAgent;
  const navigateur = /Edg\//.test(ua)     ? 'Edge'
                   : /Chrome\//.test(ua)  ? 'Chrome'
                   : /Firefox\//.test(ua) ? 'Firefox'
                   : /Safari\//.test(ua)  ? 'Safari'
                   : 'navigateur';
  const plateforme = /Android/.test(ua)          ? 'Android'
                   : /iPhone|iPad/.test(ua)      ? 'iOS'
                   : /Mac OS X/.test(ua)         ? 'Mac'
                   : /Windows/.test(ua)          ? 'Windows'
                   : /Linux/.test(ua)            ? 'Linux'
                   : '';
  return plateforme ? `${navigateur} sur ${plateforme}` : navigateur;
}

/**
 * Client d'un smartphone, d'après le User-Agent de son WebSocket (relevés du 24/10/2026) :
 * l'app Kleon Studio envoie `RemoteDevice/<build> CFNetwork/… Darwin/…` sur iOS ; la page
 * web, un UA navigateur `Mozilla/5.0 …`. Kleon Studio Android n'a encore jamais été relevé :
 * toute app `RemoteDevice/` hors Darwin lui est attribuée par défaut.
 */
function deviceClientLabel(ua?: string): string {
  if (!ua) return '';
  if (/^RemoteDevice\//.test(ua)) return /Darwin\//.test(ua) ? 'Kleon Studio iOS' : 'Kleon Studio Android';
  if (/^Mozilla\//.test(ua)) {
    const plateforme = /Android/.test(ua)     ? 'Android'
                     : /iPhone|iPad/.test(ua) ? 'iOS'
                     : /Mac OS X/.test(ua)    ? 'Mac'
                     : /Windows/.test(ua)     ? 'Windows'
                     : '';
    return plateforme ? `Web (${plateforme})` : 'Web';
  }
  return 'Inconnu';
}

export interface UseLiveStreamSetupOptions {
  _sharedRefs?: SignalingRefs;
  externalSources: { id: string; label: string; url: string }[];
  channelActive?: boolean;
  channelStreamPath?: string;
  /** uuid de la chaîne ouverte : cloisonne la mémoire du deck et des raccourcis. */
  channelUuid?: string | null;
  /**
   * Rôle de cette régie sur le canal de contrôle. 'pending' : on ne sait pas encore
   * (pas de connexion — se connecter d'abord comme porteuse volerait la main avant
   * de basculer en observatrice) ; 'silent' : superadmin chez un autre créateur.
   */
  observerMode?: 'pending' | 'silent' | 'normal';
  /** Nom affiché de la chaîne ouverte : uniquement pour l'onglet Logs du créateur. */
  channelName?: string;
  // 'activating' / 'activating_restricted' : la chaîne vient d'être activée mais l'infra
  // (container, HAProxy, MediaMTX) n'est pas encore prête — cf. probeWhipCodec, qui publie
  // pour de vrai sur cette adresse dès le montage et tombait sur 410 pendant cette fenêtre.
  channelStatus?: string;
  onHandleDeckSwitch  : (source: VideoSource) => void;
  onToggleImageSlot   : (slotId: string) => void;
  onTriggerSoundSlot  : (slot: any) => void;
  onStartVideoOverlay : (slot: any) => void;
  onTriggerJingleSlot : (slot: any) => void;
  onHandleDeckCutRef  : React.MutableRefObject<() => void>;
  /**
   * Nettoyage local à faire quand admin-api arrête l'enregistrement de son propre
   * chef (quota de durée d'enregistrement épuisé, cf. onDurationNotice ci-dessous).
   * Par ref : défini dans LiveStreamManager, donc après cet appel — même pattern
   * que onHandleDeckCutRef.
   */
  onRecordingLimitReachedRef?: React.MutableRefObject<() => void>;
  /** Jumeaux « audio du programme » prêts côté serveur (angles de téléphones), cf. angleAudio.ts. */
  onAngleAudioStateRef?: React.MutableRefObject<(ready: string[]) => void>;
  imageSlotsRef        : React.MutableRefObject<any[]>;
  soundSlotsRef        : React.MutableRefObject<any[]>;
  videoOverlaySlotsRef : React.MutableRefObject<any[]>;
  jingleSlotsRef       : React.MutableRefObject<any[]>;
  deckSlotsRef         : React.MutableRefObject<(VideoSource | null)[]>;
  startWebRTCStreaming : () => Promise<void>;
  canvasStreamRef      : React.MutableRefObject<MediaStream | null>;
  audioMixerDestRef    : React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
  currentStreamRef     : React.MutableRefObject<MediaStream | null>;
  sceneVideoSources?   : VideoSource[];
  /**
   * Recharge la liste des sources externes de la chaîne (HLS, Xtream, RTMP).
   * Par ref : elle est définie dans LiveStreamManager, donc après cet appel.
   */
  reloadExternalSourcesRef?: React.MutableRefObject<(reason?: string) => void>;
  poweredOffSlots?     : Set<string>;   // ← AJOUT v2.44
}

export function useLiveStreamSetup(options: UseLiveStreamSetupOptions) {
  const {
    _sharedRefs, externalSources,
    channelActive = true,
    channelStatus,
    channelStreamPath,
    channelUuid = null,
    observerMode = 'normal',
    channelName,
    reloadExternalSourcesRef,
    onHandleDeckSwitch, onToggleImageSlot, onTriggerSoundSlot,
    onStartVideoOverlay, onTriggerJingleSlot,
    onHandleDeckCutRef,
    onRecordingLimitReachedRef,
    onAngleAudioStateRef,
    imageSlotsRef, soundSlotsRef, videoOverlaySlotsRef, jingleSlotsRef,
    deckSlotsRef, startWebRTCStreaming,
    canvasStreamRef, audioMixerDestRef, currentStreamRef,
    sceneVideoSources = [],
    poweredOffSlots = new Set<string>(),   // ← AJOUT v2.44
  } = options;

  const {
    peerConnectionRef,
    selectedVideoSource, setSelectedVideoSource,
    setVideoSources, videoSources, settings,
    setRemoteConnections, remoteConnections,
    setNegotiatedWhipCodec, negotiatedWhipCodec,
    setSelectedAudio, selectedAudio,
    setIsStreaming,
    layoutSlots: allLayoutSlots,
    screenShareSlots,
    screenShareStreams,
    registerControlSocket,
  } = useMediaStream();

  const {
    addSdpLogRegie, showAlert, showConfirm, setWhipInfo,
  } = useProductionControlRoom();

  const { registry, sources: registrySources } = useSourceRegistry();

  const admin         = useTypedSelector ( state => state.authenticationReducer.authentication.admin );
  const dispatchAdmin = useTypedDispatch ();

  // ── Handles des borrows pour les previews ────────────────────────────────
  const previewBorrowHandlesRef = useRef<Map<string, BorrowHandle>>(new Map());

  const [devices,       setDevices]       = useState<{ video: MediaDeviceInfo[]; audio: MediaDeviceInfo[] }>({ video: [], audio: [] });
  const [remoteDevices, setRemoteDevices] = useState<RemoteDevice[]>([]);
  // Lu depuis l'élagage du deck, dont les dépendances ne suivent pas cet état.
  const remoteDevicesRef = useRef<RemoteDevice[]>([]);

  // ── Contrôle de la régie ────────────────────────────────────────────────────
  //
  // Plusieurs régies peuvent être ouvertes sur la même chaîne ; une seule pilote
  // les devices. Avant le 15/08/2026 rien ne le disait : la seconde évinçait la
  // première en silence, et toutes deux se croyaient aux commandes.
  //
  // On part porteur du contrôle : c'est le cas de très loin le plus fréquent (une
  // seule régie ouverte), et cela évite d'afficher « en attente » pendant les
  // quelques centaines de millisecondes qui précèdent le premier control_state.
  // Un microservice antérieur n'en envoie jamais — l'interface reste alors dans
  // l'état d'avant, sans bandeau.
  const [controlState, setControlState] = useState<RegieControlState>({
    isHolder: true, holderLabel: null, holderAddr: null, regieCount: 1,
    sessionId: null, peers: [],
  });
  const controlStateRef = useRef(controlState);
  useEffect(() => { controlStateRef.current = controlState; }, [controlState]);

  // ── La main se demande (23/08/2026) ────────────────────────────────────────
  //
  // « Prendre le contrôle » n'évince plus personne : la question est posée au
  // porteur, qui répond. Deux états, jamais actifs en même temps sur une même
  // régie — on ne demande pas ce qu'on détient déjà :
  //
  //   controlRequest         : la demande que NOUS avons émise, en attente ;
  //   incomingControlRequest : celle qu'on nous adresse, à nous qui pilotons.
  //
  // `canForce` ne s'ouvre qu'au bout du délai de grâce, et UNIQUEMENT sur un
  // silence : un refus explicite clôt la demande (le serveur le refuserait de
  // toute façon — cf. force_control, le garde-fou n'est pas ici).
  const [controlRequest, setControlRequest] =
    useState<{ holderLabel: string | null; holderAddr: string | null; canForce: boolean } | null>(null);
  const [incomingControlRequest, setIncomingControlRequest] =
    useState<RegieControlRequest | null>(null);
  const controlGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const armerDelaiDeForcage = useCallback((graceS: number) => {
    if (controlGraceTimerRef.current) clearTimeout(controlGraceTimerRef.current);
    controlGraceTimerRef.current = setTimeout(() => {
      controlGraceTimerRef.current = null;
      // Mise à jour fonctionnelle : entre l'armement et l'échéance, la demande a
      // pu être close (réponse, bascule, fermeture). On n'en ressuscite pas une.
      setControlRequest(prev => (prev ? { ...prev, canForce: true } : prev));
    }, Math.max(0, graceS) * 1000);
  }, []);

  const cloreDemandeDeControle = useCallback(() => {
    if (controlGraceTimerRef.current) {
      clearTimeout(controlGraceTimerRef.current);
      controlGraceTimerRef.current = null;
    }
    setControlRequest(null);
  }, []);

  useEffect(() => () => {
    if (controlGraceTimerRef.current) clearTimeout(controlGraceTimerRef.current);
  }, []);
  const [codecProbeState, setCodecProbeState] = useState<'idle' | 'probing' | 'done' | 'error'>(
    negotiatedWhipCodec ? 'done' : 'idle'
  );

  const _localWsRef                  = useRef<WebSocket | null>(null);
  const _localWsReconnectTimeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const _localWsHbIntervalRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const _localWsPongTimeoutRef       = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const _localIceBufRef              = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const _localRemoteDescRef   = useRef<Map<string, boolean>>(new Map());
  const _localRemoteConnsRef  = useRef<Map<string, RemoteConnection>>(remoteConnections);
  const _localDevInfoRef      = useRef<Map<string, RemoteDeviceInfo>>(new Map());
  const _localDevCodecsRef    = useRef<Map<string, string[]>>(new Map());
  const _localRequiredRef     = useRef<{ codec: string; profile: string } | null>(null);
  const _localWhipOfferRef    = useRef<string | undefined>(undefined);
  const _localWhipAnswerRef   = useRef<string | undefined>(undefined);
  const _localWhipStatsRef    = useRef<ReturnType<typeof setInterval> | null>(null);

  const signalingWsRef           = (_sharedRefs?.wsRef                    ?? _localWsRef)                  as React.MutableRefObject<WebSocket | null>;
  const wsReconnectTimeoutRef    = (_sharedRefs?.wsReconnectTimeoutRef    ?? _localWsReconnectTimeoutRef)  as React.MutableRefObject<ReturnType<typeof setTimeout>  | null>;
  const wsHbIntervalRef          = (_sharedRefs?.wsHbIntervalRef          ?? _localWsHbIntervalRef)        as React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  const wsPongTimeoutRef         = (_sharedRefs?.wsPongTimeoutRef         ?? _localWsPongTimeoutRef)       as React.MutableRefObject<ReturnType<typeof setTimeout>  | null>;
  const iceCandidateBufferRef    = (_sharedRefs?.iceCandidateBufferRef    ?? _localIceBufRef)        as React.MutableRefObject<Map<string, RTCIceCandidateInit[]>>;
  const remoteDescSetRef         = (_sharedRefs?.remoteDescSetRef         ?? _localRemoteDescRef)    as React.MutableRefObject<Map<string, boolean>>;
  const remoteConnectionsRef     = (_sharedRefs?.remoteConnectionsRef     ?? _localRemoteConnsRef)   as React.MutableRefObject<Map<string, RemoteConnection>>;
  const remoteDeviceInfoRef      = (_sharedRefs?.remoteDeviceInfoRef      ?? _localDevInfoRef)       as React.MutableRefObject<Map<string, RemoteDeviceInfo>>;
  const deviceSupportedCodecsRef = (_sharedRefs?.deviceSupportedCodecsRef ?? _localDevCodecsRef)    as React.MutableRefObject<Map<string, string[]>>;
  const requiredCodecRef         = (_sharedRefs?.requiredCodecRef         ?? _localRequiredRef)      as React.MutableRefObject<{ codec: string; profile: string } | null>;
  const whipOfferSdpRef          = (_sharedRefs?.whipOfferSdpRef          ?? _localWhipOfferRef)    as React.MutableRefObject<string | undefined>;
  const whipAnswerSdpRef         = (_sharedRefs?.whipAnswerSdpRef         ?? _localWhipAnswerRef)   as React.MutableRefObject<string | undefined>;
  const whipStatsIntervalRef     = (_sharedRefs?.whipStatsIntervalRef     ?? _localWhipStatsRef)    as React.MutableRefObject<ReturnType<typeof setInterval> | null>;

  const channelActiveRef = useRef<boolean>(channelActive);
  useEffect(() => { channelActiveRef.current = channelActive; }, [channelActive]);

  const connectToDeviceRef          = useRef<(id: string) => Promise<void>>(async () => {});
  const connectToSignalingServerRef = useRef<() => void>(() => {});

  // ── v2.45 : ref surchargeable par LiveStreamManager pour passer les callbacks intercomm ──
  const handleSignalingMessageRef = useRef<(msg: any) => Promise<void>>(async () => {});

  // ── v2.46 : nettoyage de la source `remote-<id>` (deck, preview, audio, registry,
  //            intercomm). Le code vit dans LiveStreamManager — seul endroit qui a
  //            handleRemoveSource et cleanupIntercommForDevice — et est branché ici
  //            par ref, comme handleSignalingMessageRef. ──
  const remoteDeviceTeardownRef = useRef<(device_id: string) => void>(() => {});

useEffect(() => { 
  console.log('[SETUP] remoteConnections changé — taille=', remoteConnections.size, 
    'devices=', Array.from(remoteConnections.keys()).join(','));
  remoteConnectionsRef.current = remoteConnections; 
}, [remoteConnections]);


  useEffect(() => {
    if (negotiatedWhipCodec && !requiredCodecRef.current) {
      const codec   = (negotiatedWhipCodec as any).codec   ?? (negotiatedWhipCodec as any).videoCodec   ?? '';
      const profile = (negotiatedWhipCodec as any).profile ?? (negotiatedWhipCodec as any).videoProfile ?? '';
      if (codec) requiredCodecRef.current = { codec, profile };
    }
  }, [negotiatedWhipCodec]);

  const [previewStreams,        setPreviewStreams]        = useState<Map<string, MediaStream>>(new Map());
  const previewStreamsRef                                 = useRef<Map<string, MediaStream>>(new Map());
  const [deckPreviewSourceId,   setDeckPreviewSourceId]  = useState<string | null>(null);

  const deckPreviewSourceIdRef = useRef<string | null>(null);
  useEffect(() => { deckPreviewSourceIdRef.current = deckPreviewSourceId; }, [deckPreviewSourceId]);

  const deckPreviewVideoRef = useRef<HTMLVideoElement>(null);
  const deckPreviewInitRef  = useRef(false);

  const [deckSlots,       setDeckSlots]       = useState<(VideoSource | null)[]>(Array(SLOT_COUNT).fill(null));
  const [deckSlotLayouts, setDeckSlotLayouts] = useState<(LayoutSlot | null)[]>(Array(SLOT_COUNT).fill(null));
  const deckSlotLayoutsRef = useRef<(LayoutSlot | null)[]>(Array(SLOT_COUNT).fill(null));

  const deckInitDone     = useRef(false);
  const lastSourceIdsRef = useRef<string>('');

  // ── Mémoire du deck par chaîne (voir deck_persistence.ts) ────────────────────
  // `deckEntriesRef` = intentions par emplacement ; un emplacement vide qui garde
  // son intention est RÉSERVÉ : le remplissage automatique n'y touche pas.
  const deckChannelUuidRef = useRef<string | null>(channelUuid);
  useEffect(() => { deckChannelUuidRef.current = channelUuid; }, [channelUuid]);
  const deckEntriesRef   = useRef<DeckEntries>(Array(SLOT_COUNT).fill(null));
  const deckEntriesReady = useRef(false);   // chargées au démarrage du deck : rien n'est écrit avant
  // Incrémenté quand la sauvegarde serveur vient d'être reprise : relance le placement.
  const [deckRestoreTick, setDeckRestoreTick] = useState(0);
  // Sources « audio seul » posées par l'opérateur (cf. utils_audioOnlySource.ts) : elles
  // n'existent dans `videoSources` que par cette liste, mémorisée par chaîne.
  const [audioOnlySources, setAudioOnlySources] = useState<VideoSource[]>(() => loadAudioOnlySources(channelUuid));
  const audioOnlySourcesRef = useRef(audioOnlySources);
  useEffect(() => {
    const list = loadAudioOnlySources(channelUuid);
    audioOnlySourcesRef.current = list;
    setAudioOnlySources(list);
  }, [channelUuid]);

  const saveDeckSlotsConfig = (
    slots      : (VideoSource | null)[],
    layoutSlots: (LayoutSlot | null)[],
  ): void => {
    const uuid = deckChannelUuidRef.current;
    if (!uuid || !deckEntriesReady.current) return;
    deckEntriesRef.current = mergeDeckEntries(deckEntriesRef.current, slots, layoutSlots);
    saveDeckEntries(uuid, deckEntriesRef.current);
    saveKnownSources(uuid, everAssignedToSlotRef.current);
    scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
  };
  /**
   * Une source a changé d'identifiant (caméra RTMP recréée avec une autre clé) : la place
   * qui lui était réservée et sa mémoire de « source connue » la suivent.
   */
  const remapSourceIds = (map: Map<string, string>): void => {
    if (map.size === 0) return;
    deckEntriesRef.current = deckEntriesRef.current.map(e =>
      e && e.kind !== 'layout' ? { ...e, ids: e.ids.map(id => map.get(id) ?? id) } : e);
    map.forEach((to, from) => { if (everAssignedToSlotRef.current.delete(from)) everAssignedToSlotRef.current.add(to); });
    const uuid = deckChannelUuidRef.current;
    if (uuid) { saveDeckEntries(uuid, deckEntriesRef.current); saveKnownSources(uuid, everAssignedToSlotRef.current); }
    setDeckRestoreTick(t => t + 1);   // une source déjà présente reprend sa place
  };
  /**
   * Modifie une source audio seul déjà posée (son image, `audioVisual`). La liste est
   * réécrite et la sauvegarde serveur relancée ; `videoSources` est reconstruite, et
   * l'élagage du deck remplace l'objet porté par l'emplacement par le neuf.
   */
  const updateAudioOnlySource = useCallback((sourceId: string, patch: Partial<Pick<VideoSource, 'audioVisual' | 'label'>>) => {
    const list = audioOnlySourcesRef.current;
    if (!list.some(s => s.id === sourceId)) return;
    const next = list.map(s => (s.id === sourceId ? { ...s, ...patch } : s));
    audioOnlySourcesRef.current = next;
    setAudioOnlySources(next);
    const uuid = deckChannelUuidRef.current;
    saveAudioOnlySources(uuid, next);
    if (uuid) scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
  }, []);
  /** Ces sources sont connues : le remplissage automatique ne les pose plus sur un bouton. */
  const markSourcesKnown = (ids: string[]): void => {
    ids.forEach(id => everAssignedToSlotRef.current.add(id));
    saveKnownSources(deckChannelUuidRef.current, everAssignedToSlotRef.current);
  };
  /** Décision de l'opérateur : l'emplacement n'est plus réservé à personne. */
  const forgetDeckEntry = (slotIndex: number): void => { deckEntriesRef.current[slotIndex] = null; };
  /** Toute décision de l'opérateur sur le deck : il n'est plus un simple remplissage par défaut. */
  const noteDeckEdit = (): void => markDeckAuto(deckChannelUuidRef.current, false);

  // ── v2.43 : tracking des sources déjà assignées à un slot ────────────────
  const everAssignedToSlotRef = useRef<Set<string>>(new Set());
  // Devices pour lesquels le repli SFU → P2P a déjà été signalé à l'opérateur.
  // Une reconnexion de device rejoue connectToDevice : sans ce garde, chaque
  // tentative rouvrirait la même alerte.
  const sfuFallbackNotifiedRef = useRef<Set<string>>(new Set());

  // ── v2.41 : resetDeckInit ─────────────────────────────────────────────────
  const resetDeckInit = useCallback(() => {
    previewBorrowHandlesRef.current.forEach(handle => {
      try { handle.release(); } catch {}
    });
    previewBorrowHandlesRef.current.clear();

    previewStreamsRef.current.clear();
    setPreviewStreams(new Map());

    deckInitDone.current       = false;
    deckEntriesReady.current   = false;
    deckPreviewInitRef.current = false;
    lastSourceIdsRef.current   = '';
    everAssignedToSlotRef.current.clear();
  }, []);

/**
   * Remet le deck comme à la création de la chaîne : mémoire d'emplacements oubliée
   * (locale ; la sauvegarde automatique serveur suit à l'écriture du deck par défaut),
   * emplacements vidés, puis remplissage par défaut (webcams locales, Playlist…) au
   * prochain passage de l'effet d'initialisation, que le compteur relance.
   */
  const resetDeckToDefaults = useCallback(() => {
    const uuid = deckChannelUuidRef.current;
    if (uuid) { try { localStorage.removeItem(deckEntriesKey(uuid)); } catch {} clearKnownSources(uuid); }
    // Les sources « audio seul » n'existent que par leur emplacement : plus d'emplacement, plus de source.
    audioOnlySourcesRef.current = [];
    setAudioOnlySources([]);
    saveAudioOnlySources(uuid, []);
    deckEntriesRef.current = Array(SLOT_COUNT).fill(null);
    resetDeckInit();
    deckEntriesReady.current = false;
    releasedRemoteSlotsRef.current.clear();
    const empty = Array(SLOT_COUNT).fill(null);
    deckSlotsRef.current       = [...empty];
    deckSlotLayoutsRef.current = [...empty];
    setDeckSlots([...empty]);
    setDeckSlotLayouts([...empty]);
    setDeckRestoreTick(t => t + 1);
  }, [resetDeckInit]);

const forgetSlotAssignment = useCallback((sourceId: string) => {
  everAssignedToSlotRef.current.delete(sourceId);
}, []);

  /**
   * Emplacements de deck rendus SUR DÉCISION, et non par disparition de la source.
   *
   * L'élagage plus bas vide un emplacement dès que sa source manque à la liste, ne
   * serait-ce que le temps d'un rendu. Or la liste des sources distantes est
   * reconstruite à chaque battement de `remoteConnections` : entre la fermeture de
   * l'ancienne PeerConnection et l'arrivée de la nouvelle piste, il existe toujours
   * un rendu où la caméra n'est ni connectée ni « en attente ». L'emplacement
   * tombait là, à la reconnexion même — le défaut du 2026-08-31, celui que le délai
   * de grâce ne pouvait pas expliquer.
   *
   * Un emplacement `remote-*` n'est donc plus rendu que sur décision explicite :
   * expiration du délai de grâce, départ annoncé, suppression de l'appareil. Tant
   * que l'appareil reste inscrit, sa place lui appartient.
   */
  const releasedRemoteSlotsRef    = useRef<Set<string>>(new Set());
  // Appareils qui se sont manifestés au moins une fois dans cette session de régie.
  const seenConnectedRemoteRef = useRef<Set<string>>(new Set());

  useEffect(() => { remoteDevicesRef.current = remoteDevices; }, [remoteDevices]);

  const releaseRemoteDeckSlot = useCallback((device_id: string) => {
    const sourceId = `remote-${device_id}`;
    releasedRemoteSlotsRef.current.add(sourceId);
    seenConnectedRemoteRef.current.delete(sourceId);
    everAssignedToSlotRef.current.delete(sourceId);
    setDeckSlots(prev => {
      if (!prev.some(s => s?.id === sourceId)) return prev;
      const next = prev.map((s, i) => {
        if (s?.id !== sourceId) return s;
        forgetDeckEntry(i);
        noteDeckEdit();
        return null;
      });
      deckSlotsRef.current = next;
      saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      return next;
    });
  }, []);

  /**
   * L'appareil s'est manifesté : sa place devient tenable.
   *
   * Le passage par ici est ce qui distingue une caméra qui va et vient d'un
   * appareil simplement inscrit et jamais allumé. Sans lui, un emplacement
   * enregistré dans la configuration du deck afficherait « EN ATTENTE » à
   * l'ouverture de la régie pour un téléphone qui dort dans un tiroir.
   */
  const holdRemoteDeckSlot = useCallback((device_id: string) => {
    const sourceId = `remote-${device_id}`;
    releasedRemoteSlotsRef.current.delete(sourceId);
    seenConnectedRemoteRef.current.add(sourceId);
  }, []);

  /**
   * Cet emplacement appartient-il encore à un appareil vivant dans la liste ?
   *
   * « Inscrit » et non « connecté » : c'est tout l'objet de la garde. Un appareil
   * supprimé sort de `remoteDevices` et sa place se libère au rendu suivant.
   */
  const isHeldRemoteSlot = useCallback((sourceId: string) => {
    if (!sourceId.startsWith('remote-')) return false;
    if (releasedRemoteSlotsRef.current.has(sourceId)) return false;
    if (!seenConnectedRemoteRef.current.has(sourceId)) return false;
    const device_id = sourceId.slice('remote-'.length);
    return remoteDevicesRef.current.some(d => d.device_id === device_id);
  }, []);

  /**
   * Appareils tombés dont on garde la place.
   *
   * Une déconnexion vidait l'emplacement du deck sans un mot : au milieu d'une
   * mosaïque de douze, une vignette qui s'évapore ne se remarque qu'au moment où
   * l'on voulait prendre cette caméra. Et le remplissage automatique la relogeait
   * ensuite dans le premier emplacement libre, jamais le sien.
   *
   * Tant qu'un appareil figure ici, sa source `remote-<id>` continue d'exister —
   * sans flux — donc son emplacement lui reste réservé et il le retrouve à son
   * retour. Passé le délai de grâce, on renonce : la place est rendue, et la
   * négociation cesse d'être relancée.
   */
  const [awaitingDevices, setAwaitingDevices] = useState<Set<string>>(new Set());
  // Doublure synchrone : les fonctions ci-dessous sont appelées depuis la
  // signalisation, hors de tout rendu, et doivent lire l'ensemble tel qu'il est à
  // cet instant — pas tel qu'il était à la création de la fermeture.
  const awaitingDevicesRef = useRef<Set<string>>(new Set());
  const awaitingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());


  const handleDeckSlotChange = useCallback((slotIndex: number, newSource: VideoSource | null) => {
    // Liste des sources audio seul : celle qu'on pose y entre, celle qu'on retire du
    // deck en sort (elle n'a pas d'autre existence). Écrite AVANT le deck, pour que la
    // sauvegarde automatique que déclenche saveDeckSlotsConfig l'emporte à jour.
    const replaced = deckSlotsRef.current[slotIndex];
    let audioOnly = audioOnlySourcesRef.current;
    if (replaced?.type === 'audio' && replaced.id !== newSource?.id
        && !deckSlotsRef.current.some((s, i) => i !== slotIndex && s?.id === replaced.id)) {
      audioOnly = audioOnly.filter(s => s.id !== replaced.id);
    }
    if (newSource?.type === 'audio' && !audioOnly.some(s => s.id === newSource.id)) {
      audioOnly = [...audioOnly, newSource];
    }
    if (audioOnly !== audioOnlySourcesRef.current) {
      audioOnlySourcesRef.current = audioOnly;
      setAudioOnlySources(audioOnly);
      saveAudioOnlySources(deckChannelUuidRef.current, audioOnly);
    }
    setDeckSlots(prev => {
      const next = [...prev];
      if (newSource) {
        everAssignedToSlotRef.current.add(newSource.id);
        for (let i = 0; i < next.length; i++) {
          if (i !== slotIndex && next[i]?.id === newSource.id) next[i] = null;
        }
      }
      next[slotIndex] = newSource;
      noteDeckEdit();
      if (!newSource) forgetDeckEntry(slotIndex);
      deckSlotsRef.current = next;
      saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      return next;
    });
    if (newSource) {
      setDeckSlotLayouts(prev => {
        if (!prev[slotIndex]) return prev;
        const next = [...prev];
        next[slotIndex] = null;
        deckSlotLayoutsRef.current = next;
        return next;
      });
    }
  }, [deckSlotsRef]);

  /**
   * Échange deux emplacements du deck — le glisser-déposer des mini-vues du Multiview.
   *
   * ÉCHANGE et non insertion-décalage : les emplacements sont numérotés et le
   * pupitre associe chaque numéro à une touche (cf. keyMap). Déplacer la source 3
   * en 6 par décalage renumérote tout ce qui se trouve entre les deux — sur une
   * régie en direct, l'opérateur perdrait d'un coup la moitié de ses repères pour
   * un seul déplacement voulu. L'échange ne touche que les deux emplacements.
   *
   * Ce qui voyage ensemble, et doit voyager ensemble : la source (`deckSlots`) et
   * la scène qui peut occuper l'emplacement à sa place (`deckSlotLayouts`). Les
   * dissocier laisserait un emplacement portant la scène de l'autre.
   *
   * `slotConfigs` (libellés personnalisés du Multiview) est réordonné par
   * l'appelant : il vit dans LiveStreamManager, pas ici.
   */
  const handleDeckSlotsSwap = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setDeckSlots(prev => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      [ next[fromIndex] , next[toIndex] ] = [ next[toIndex] , next[fromIndex] ];
      const e = deckEntriesRef.current;
      [ e[fromIndex] , e[toIndex] ] = [ e[toIndex] , e[fromIndex] ];
      noteDeckEdit();
      deckSlotsRef.current = next;
      saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      return next;
    });

    setDeckSlotLayouts(prev => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      [ next[fromIndex] , next[toIndex] ] = [ next[toIndex] , next[fromIndex] ];
      deckSlotLayoutsRef.current = next;
      // Second enregistrement, avec les deux tableaux à jour : le premier, dans
      // l'updater ci-dessus, a écrit les scènes telles qu'elles étaient AVANT
      // l'échange — les deux updaters ne peuvent pas voir l'état l'un de l'autre.
      saveDeckSlotsConfig(deckSlotsRef.current, next);
      return next;
    });
  }, [deckSlotsRef, deckSlotLayoutsRef]);

  /**
   * Garde la place d'une source ABSENTE : l'emplacement reste vide mais réservé (le
   * remplissage automatique n'y touche pas), et la source le retrouve à son retour.
   * Mis en file APRÈS l'éventuel vidage de l'emplacement, dont l'updater efface
   * l'intention — d'où le passage par setDeckSlots.
   */
  const reserveDeckSlot = useCallback((slotIndex: number, entry: { kind: 'device' | 'remote' | 'other'; id: string }) => {
    setDeckSlots(prev => {
      deckEntriesRef.current[slotIndex] = { kind: entry.kind, ids: [entry.id] };
      saveDeckSlotsConfig(prev, deckSlotLayoutsRef.current);
      return prev;
    });
  }, []);

  const handleDeckSlotLayoutChange = useCallback((slotIndex: number, layout: LayoutSlot | null) => {
    setDeckSlotLayouts(prev => {
      const next = [...prev];
      next[slotIndex] = layout;
      noteDeckEdit();
      if (!layout) forgetDeckEntry(slotIndex);
      deckSlotLayoutsRef.current = next;
      return next;
    });
    setDeckSlots(prev => {
      const next = [...prev];
      if (layout) next[slotIndex] = null;
      deckSlotsRef.current = next;
      saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      return next;
    });
  }, [deckSlotsRef]);

  /**
   * Renomme une source déjà posée sur le pupitre — rien d'autre.
   *
   * Un alias n'est qu'un nom d'affichage : le remplacer en retirant puis
   * réenregistrant la source couperait le flux, à l'antenne compris, pour une
   * étiquette. Les cases du deck gardent une copie de la source qu'on leur a
   * assignée ; sans cette retouche, elles continueraient d'afficher l'ancien nom
   * jusqu'au prochain rechargement de la régie.
   */
  const renameDeckSlotSource = useCallback((sourceId: string, label: string) => {
    setDeckSlots(prev => {
      if (!prev.some(slot => slot?.id === sourceId && slot.label !== label)) return prev;
      const next = prev.map(slot =>
        slot?.id === sourceId ? { ...slot, label } : slot
      );
      deckSlotsRef.current = next;
      saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      return next;
    });
  }, [deckSlotsRef, deckSlotLayoutsRef]);

  const loadKeyMapCompat = (): DeckKeyMap => {
    const km = loadKeyMap(channelUuid);
    return km.jingle ? km : { ...km, jingle: [null, null, null, null, null, null, null, null] };
  };
  const [keyMap, setKeyMap] = useState<DeckKeyMap>(loadKeyMapCompat);
  const keyMapRef           = useRef<DeckKeyMap>(loadKeyMapCompat());
  useEffect(() => { keyMapRef.current = keyMap; }, [keyMap]);

  const handleKeyMapChange = useCallback((km: DeckKeyMap) => {
    setKeyMap(km); saveKeyMap(km, deckChannelUuidRef.current);
    const uuid = deckChannelUuidRef.current;
    if (uuid) scheduleAutosave(uuid, () => readChannelSnapshot(uuid));
  }, []);

  // Sauvegarde serveur reprise après le démarrage du deck : on relit ce qu'elle a
  // apporté (placement des sources, raccourcis) et on relance le placement.
  useEffect(() => {
    const onRestored = (e: Event) => {
      const detail = (e as CustomEvent<DeckRestoredDetail>).detail;
      if (detail?.uuid !== deckChannelUuidRef.current) return;
      if (detail.settings) {
        const list = loadAudioOnlySources(detail.uuid);
        audioOnlySourcesRef.current = list;
        setAudioOnlySources(list);
      }
      const km = loadKeyMap(deckChannelUuidRef.current);
      setKeyMap(km.jingle ? km : { ...km, jingle: [null, null, null, null, null, null, null, null] });
      if (detail.deck && deckEntriesReady.current) {
        // Le deck en place n'était que le remplissage par défaut : on le refait
        // depuis l'arrangement du serveur (la phase d'initialisation le relit).
        deckInitDone.current     = false;
        lastSourceIdsRef.current = '';
        everAssignedToSlotRef.current.clear();
      }
      setDeckRestoreTick(t => t + 1);
    };
    window.addEventListener(DECK_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(DECK_RESTORED_EVENT, onRestored);
  }, []);

  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  /**
   * Le chemin de la chaîne ouverte — ou rien. Il n'y a plus de repli.
   *
   * Cette valeur retombait autrefois sur `settings.streamPath`, qui portait en dur
   * l'UUID d'une chaîne de production : tant que l'UUID de la chaîne ouverte n'était
   * pas résolu, la régie lisait et écrivait sur CETTE chaîne-là. Le réglage a été
   * supprimé (cf. MediaStreamContext) : le chemin d'une chaîne ne se configure pas,
   * il EST la chaîne ouverte.
   */
  const effectiveStreamPath = channelStreamPath?.trim() || null;
  const effectiveStreamPathRef = useRef<string | null>(effectiveStreamPath);
  useEffect(() => { effectiveStreamPathRef.current = effectiveStreamPath; }, [effectiveStreamPath]);

  /**
   * Chemin des opérations sur les smartphones — SANS repli, contrairement à
   * `effectiveStreamPath`.
   *
   * Les devices sont enregistrés dans le microservice de LA chaîne : chaque
   * chaîne a son container, et `remoteDevices` y est un registre local. Retomber
   * sur `settings.streamPath` ferait donc lire le registre d'une AUTRE chaîne —
   * et ce n'est pas théorique, sa valeur par défaut est l'UUID en dur d'une
   * chaîne de production (cf. DEFAULT_SETTINGS dans MediaStreamContext). La
   * régie afficherait alors les smartphones de cette chaîne-là sous l'identité
   * de celle qu'on a ouverte, et un « Supprimer » y toucherait pour de bon.
   *
   * Sans chaîne identifiée : aucun appel, liste vide. Ne rien afficher est le
   * seul comportement honnête ici.
   */
  const deviceStreamPath = channelStreamPath?.trim() || null;
  const deviceStreamPathRef = useRef<string | null>(deviceStreamPath);
  useEffect(() => { deviceStreamPathRef.current = deviceStreamPath; }, [deviceStreamPath]);

  /**
   * Chemin de la chaîne pour une opération d'ÉCRITURE sur un smartphone.
   * Lève plutôt que de retomber ailleurs : créer, supprimer ou modifier une fiche
   * dans le registre d'une autre chaîne serait une action destructrice silencieuse.
   */
  const requireDeviceStreamPath = useCallback((): string => {
    const path = deviceStreamPathRef.current;
    if (!path) throw new Error("Aucune chaîne identifiée : opération sur les smartphones refusée.");
    return path;
  }, []);

  const videoRowRef = useRef(buildVideoRow(Array(SLOT_COUNT).fill(null), ''));
  useEffect(() => {
    videoRowRef.current = buildVideoRow(deckSlotsRef.current, effectiveStreamPathRef.current);
  }, [deckSlots]);

  const buildSignalingRefs = useCallback((): SignalingRefs => ({
    wsRef                    : signalingWsRef,
    wsReconnectTimeoutRef,
    wsHbIntervalRef,
    wsPongTimeoutRef,
    peerConnectionRef,
    iceCandidateBufferRef,
    remoteDescSetRef,
    remoteConnectionsRef,
    remoteDeviceInfoRef,
    deviceSupportedCodecsRef,
    requiredCodecRef,
    whipOfferSdpRef,
    whipAnswerSdpRef,
    whipStatsIntervalRef,
    currentStreamRef,
    canvasStreamRef,
    audioMixerDestRef,
  }), [
    signalingWsRef, wsReconnectTimeoutRef, wsHbIntervalRef, wsPongTimeoutRef,
    peerConnectionRef, iceCandidateBufferRef, remoteDescSetRef,
    remoteConnectionsRef, remoteDeviceInfoRef, deviceSupportedCodecsRef, requiredCodecRef,
    whipOfferSdpRef, whipAnswerSdpRef, whipStatsIntervalRef,
    currentStreamRef, canvasStreamRef, audioMixerDestRef,
  ]);

/*
  const buildSignalingCallbacks = useCallback((): SignalingCallbacks => ({
    addSdpLog            : addSdpLogRegie,
    setWhipInfo          : (updater) => setWhipInfo(updater),
    setIsStreaming       : (v) => setIsStreaming(v),
    setRemoteConnections : (updater) => setRemoteConnections(updater),
    setNegotiatedWhipCodec,
    setCodecProbeState,
    loadDevices          : () => loadDevicesRef.current(),
    startWebRTCStreaming  : () => startWebRTCStreaming(),
    connectToDevice      : (id) => connectToDeviceRef.current(id),
  }), [
    addSdpLogRegie, setWhipInfo, setIsStreaming,
    setRemoteConnections, setNegotiatedWhipCodec, startWebRTCStreaming,
  ]);
*/

// Même motif que loadDevicesRef : `markDeviceAwaiting` et `clearDeviceAwaiting` sont
// déclarées bien plus bas dans le corps du hook, et les citer ici — ne serait-ce que
// dans un tableau de dépendances, évalué au rendu — tomberait dans leur zone morte.
// Elles sont donc atteintes par une ref, garnie par l'effet qui suit leur définition.
const markDeviceAwaitingRef  = useRef<(device_id: string) => void>(() => {});
const clearDeviceAwaitingRef = useRef<(device_id: string) => void>(() => {});
const renewDeviceAwaitingRef = useRef<(device_id: string) => void>(() => {});

const buildSignalingCallbacks = useCallback((): SignalingCallbacks => ({
    addSdpLog            : addSdpLogRegie,
    setWhipInfo          : (updater) => setWhipInfo(updater),
    setIsStreaming       : (v) => setIsStreaming(v),
    setRemoteConnections : (updater) => setRemoteConnections(updater),
    setNegotiatedWhipCodec,
    setCodecProbeState,
    loadDevices          : () => loadDevicesRef.current(),
    startWebRTCStreaming  : () => startWebRTCStreaming(),
    connectToDevice      : (id) => connectToDeviceRef.current(id),
    injectStream         : (sourceId, stream) => registry.injectStream(sourceId, stream),
    applyDirectorRoleOnConnect : (id) => applyDirectorRoleOnConnectRef.current(id),
    applyMetricsLogOnConnect   : (id) => applyMetricsLogOnConnectRef.current(id),
    logDeviceConnected         : (id, ip, ua) => logDeviceConnectedRef.current(id, ip, ua),
    logDeviceDisconnected      : (id) => logDeviceDisconnectedRef.current(id),
    markDeviceAwaiting         : (id) => markDeviceAwaitingRef.current(id),
    clearDeviceAwaiting        : (id) => clearDeviceAwaitingRef.current(id),
    onDeviceNetworkQuality     : (id, level, sample) => handleDeviceNetworkQualityRef.current(id, level, sample),
    onDeviceGeo                : (id, report) => handleDeviceGeoRef.current(id, report),
    setControlState      : (state) => setControlState(state),
    onControlRevoked     : (takenBy, reason, takenByAddr) => {
      const qui = decrireRegie(takenBy, takenByAddr);
      // Perdre la main clôt toute question en cours : on ne répond plus de rien.
      setIncomingControlRequest(null);
      // Depuis le 23/08/2026 la main se demande : quand elle change, c'est le
      // plus souvent parce qu'on vient de l'accorder soi-même. Alerter dans ce
      // cas-là reviendrait à annoncer à l'opérateur ce qu'il vient de cliquer.
      if (reason === 'accepted') return;
      showAlert(
        reason === 'forced'
          ? `« ${qui} » a pris la main : votre régie n'a pas répondu à sa demande. `
            + 'Vos actions sur les caméras distantes ne sont plus prises en compte : '
            + 'les sources externes se sont reconnectées à elle.'
          : `Le contrôle de la régie vient d'être pris par « ${qui} ». `
            + 'Vos actions sur les caméras distantes ne sont plus prises en compte. '
            + 'Utilisez « Prendre le contrôle » pour récupérer la main.',
        'important',
      );
    },
    // Seule la reprise SUBIE est signalée. 'requested', 'accepted' et 'forced'
    // suivent un clic de l'opérateur, et 'first' est le cas d'une régie seule :
    // dans tous ces cas il sait déjà, et la disparition du bandeau suffit.
    // Alerter à chaque ouverture ferait passer le message pour du bruit, et il ne
    // serait plus lu le jour où il compte.
    onControlGranted     : (reason) => {
      cloreDemandeDeControle();
      setIncomingControlRequest(null);
      // On prend la main sur la liste de sources qu'on avait à l'ouverture de
      // l'onglet : une caméra ajoutée entre-temps par l'autre régie n'y est pas.
      // C'est le moment de la relire, avant d'agir sur elle.
      reloadExternalSourcesRef?.current?.();
      if (reason !== 'holder_left') return;
      showAlert(
        'Vous pilotez maintenant cette chaîne : la régie qui avait le contrôle '
        + "s'est fermée, la main vous revient automatiquement.",
        'important',
      );
    },
    onSourcesChanged     : (reason) => reloadExternalSourcesRef?.current?.(reason),
    onAngleAudioState    : (ready) => onAngleAudioStateRef?.current?.(ready),
    onDurationNotice     : (kind, message, scope) => {
      showAlert(message, 'important');
      // Diffusion : admin-api désactive la chaîne lui-même (le statut se relit
      // par ailleurs). Enregistrement : rien d'autre ne prévient la régie que
      // le fichier a cessé d'être écrit — sans ce nettoyage, isRecording reste
      // vrai et le compteur continue de tourner pour rien.
      if (kind === 'limit_reached' && scope === 'recording') { onRecordingLimitReachedRef?.current?.(); }
    },
    // Un admin vient de modifier un plafond de quota pendant que cette régie
    // est ouverte (cf. AdminQuotaBroadcastService côté admin-api) : on patche
    // directement le Redux local, comme le fait déjà LiveStreamManager pour
    // quotaUsed après un stop — sans attendre un refreshAdmin() complet.
    onQuotaUpdated       : (patch) => { if (admin) { dispatchAdmin(setAdmin({ ...admin, ...patch })); } },
    onControlRequested   : (request) => setIncomingControlRequest(request),
    onControlRequestPending : (holderLabel, graceS, holderAddr) => {
      setControlRequest({ holderLabel, holderAddr, canForce: false });
      armerDelaiDeForcage(graceS);
    },
    onControlRequestDenied : (reason, by, byAddr) => {
      const qui = decrireRegie(by, byAddr);
      // 'too_soon' répond à un forçage anticipé — le serveur nous rappelle que le
      // délai court encore. La demande, elle, tient toujours : la clore ici
      // effacerait le bandeau d'attente sous les yeux de l'opérateur.
      if (reason === 'too_soon') return;
      cloreDemandeDeControle();
      if (reason === 'refused') {
        showAlert(
          `« ${qui} » garde la main : votre demande a été refusée. `
          + 'Cette régie continue de voir et d\'entendre l\'antenne, mais ne la pilote pas.',
          'important',
        );
      } else if (reason === 'busy') {
        showAlert(
          `« ${qui} » a demandé la main avant vous. `
          + 'Réessayez quand sa demande aura été tranchée.',
          'important',
        );
      }
      // 'obsolete' et 'nothing_to_force' : le contrôle a bougé entre-temps. Le
      // bandeau reflète déjà la situation, une alerte de plus n'apprendrait rien.
    },
    onControlRequestCancelled : () => setIncomingControlRequest(null),
  }), [
    addSdpLogRegie, setWhipInfo, setIsStreaming,
    setRemoteConnections, setNegotiatedWhipCodec, startWebRTCStreaming, registry,
    showAlert, armerDelaiDeForcage, cloreDemandeDeControle, reloadExternalSourcesRef,
  ]);

  const alerterWsFermee = useCallback(() => {
    showAlert(
      'La connexion au serveur de signalisation est fermée : impossible d\'agir sur le '
      + 'contrôle pour le moment. Elle se rétablit toute seule, réessayez dans quelques secondes.',
      'important',
    );
  }, [showAlert]);

  /**
   * DEMANDE la main sur les devices. Le serveur pose la question au porteur et
   * nous répond control_request_pending : rien ne bascule ici. L'état n'est pas
   * présumé localement — on attend sa réponse, sinon l'interface affirmerait
   * piloter alors qu'elle est encore ignorée.
   */
  const requestControl = useCallback(() => {
    if (!requestRegieControl({ wsRef: signalingWsRef })) alerterWsFermee();
  }, [signalingWsRef, alerterWsFermee]);

  /**
   * Passe en force, faute de réponse. N'est proposé qu'après le délai de grâce et
   * JAMAIS après un refus : un refus est une réponse. Le serveur applique la même
   * règle de son côté — l'interface n'est qu'une barrière de plus.
   */
  const forceControl = useCallback(() => {
    if (!forceRegieControl({ wsRef: signalingWsRef })) alerterWsFermee();
  }, [signalingWsRef, alerterWsFermee]);

  /**
   * Réponse du porteur. L'affichage est fermé tout de suite : la question a reçu
   * sa réponse, la garder à l'écran laisserait croire qu'elle est en suspens.
   */
  const answerControlRequest = useCallback((accept: boolean) => {
    if (!answerRegieControlRequest({ wsRef: signalingWsRef }, accept)) {
      alerterWsFermee();
      return;
    }
    setIncomingControlRequest(null);
  }, [signalingWsRef, alerterWsFermee]);

  /** La demandeuse renonce : le porteur ne doit pas rester avec la question. */
  const cancelControlRequest = useCallback(() => {
    cancelRegieControlRequest({ wsRef: signalingWsRef });
    cloreDemandeDeControle();
  }, [signalingWsRef, cloreDemandeDeControle]);

  const loadDevicesImpl = useCallback(async (): Promise<RemoteDevice[]> => {
    // Chaîne inactive : pas de container, donc personne à interroger — la requête partirait
    // en 404 chez HAProxy. La liste est rechargée dès que la chaîne redevient active (effet
    // ci-dessous, qui dépend de channelActive).
    if (!channelActiveRef.current) {
      setRemoteDevices([]);
      setDirectorDevices(new Set());
      return [];
    }
    if (!deviceStreamPath) {
      console.warn('[DEVICES] Aucune chaîne identifiée : liste des smartphones laissée vide.');
      setRemoteDevices([]);
      setDirectorDevices(new Set());
      return [];
    }
    try {
      const list = await fetchRemoteDevices(settings.serverUrl, deviceStreamPath);
      setRemoteDevices(list);
      // Le rôle Réalisateur vit sur la fiche du device : la liste chargée fait
      // foi, et le registre local n'est qu'un cache pour les trois lecteurs qui
      // en dépendent. Une bascule optimiste dont le PATCH a échoué est donc
      // corrigée au chargement suivant, plutôt que de survivre à tort.
      setDirectorDevices(new Set(list.filter(d => d.is_director).map(d => d.device_id)));
      // Un device hors ligne n'a plus de mesure : la garder afficherait un
      // niveau qui ne décrit plus rien.
      setDeviceNetworkQuality(prev => {
        const vivants = new Set(list.filter(d => d.is_connected).map(d => d.device_id));
        let change = false;
        const next = new Map(prev);
        prev.forEach((_, id) => { if (!vivants.has(id)) { next.delete(id); change = true; } });
        return change ? next : prev;
      });
      return list;
    } catch (err) { console.error('Erreur devices:', err); return []; }
  }, [settings.serverUrl, deviceStreamPath]);

  const loadDevicesRef = useRef(loadDevicesImpl);
  useEffect(() => { loadDevicesRef.current = loadDevicesImpl; }, [loadDevicesImpl]);
  const loadDevices = useCallback(() => loadDevicesRef.current(), []);

  // Rechargement à tout changement de chaîne. Le chargement initial a lieu au
  // montage, où l'UUID de la chaîne peut ne pas être encore résolu : sans cet
  // effet la liste resterait vide jusqu'au premier message de signalisation.
  // Il couvre aussi le passage d'une chaîne à l'autre sans remontage.
  useEffect(() => { loadDevicesRef.current(); }, [deviceStreamPath, channelActive]);

  /**
   * @param options.silent       L'appelant affiche lui-même le compte rendu.
   * @param options.accessToken  Token déjà communiqué au porteur (mode "uuid") :
   *                             le microservice doit reprendre celui-ci, pas en tirer un autre.
   * @returns Le device créé, ou undefined si la création n'a pas eu lieu.
   */
  /**
   * Fenêtre de refus quand le quota d'appareils est plein.
   *
   * Le quota est porté par le COMPTE, pas par la chaîne : les appareils de toutes
   * les chaînes s'y imputent, et le message doit le dire — sinon l'opérateur
   * cherche en vain quel appareil supprimer sur la chaîne courante.
   *
   * Deux issues, donc deux boutons : libérer un slot, ou relever le quota.
   */
  const alertDeviceQuotaReached = (used: number, max: number) => {
    showAlert(
      `Limite d'appareils atteinte pour ce compte (${used}/${afficherLimite(max)}, toutes chaînes confondues). `
      + `Supprimez un appareil existant avant d'en ajouter un nouveau ou augmentez votre quota d'appareils.`,
      'important',
      {
        validButtonText: 'Fermer',
        extraButtonText: BUY_DEVICES_LABEL,
        onExtra        : () => openBuyDevices(showAlert),
        // La fenêtre fait 300px par défaut : « Acheter des devices » et « Fermer »
        // n'y tiennent pas côte à côte. Le `min()` est là parce qu'une largeur
        // inline l'emporte sur la media query 430px du composant : sans lui, la
        // fenêtre déborderait de l'écran sur un petit format.
        width          : 'min(560px, 92vw)',
      },
    );
  };

  const handleRegisterDevice = async (
    id: string,
    label: string,
    password: string,
    authMode: DeviceAuthMode = 'password',
    options?: { silent?: boolean; accessToken?: string },
  ): Promise<RegisteredRemoteDevice | undefined> => {
    // Pas de refus local sur admin.nbDevicesUsed : ce compteur dérive, et le serveur le
    // recompte sur la réalité avant de refuser (AdminDeviceIncrement). Un refus ici,
    // sur une valeur périmée, empêcherait la correction de jamais avoir lieu.

    // Réservation du slot AVANT création réelle du device sur le microservice,
    // pour que le quota serveur soit une vraie barrière (rollback si la
    // création échoue ensuite).
    let reserved = false;

    try {
      if (admin) {
        const reserveResult = await incrementAdminDevicesUsed(admin.id, id, label, authMode, settings.deviceTransport, channelName);
        if (reserveResult.code !== 1) {
          alertDeviceQuotaReached(admin.nbDevicesUsed, admin.nbDevicesMax);
          return;
        }
        reserved = true;
        dispatchAdmin(setAdmin({ ...admin, nbDevicesUsed: reserveResult.payload }));
      }

      const data = await registerRemoteDevice(
        settings.serverUrl, requireDeviceStreamPath(),
        { device_id: id, label, password, auth_mode: authMode, access_token: options?.accessToken },
      );

      // Confirmation seule : identifiants et lien de connexion restent consultables
      // et copiables dans la gestion des sources.
      if (!options?.silent) showAlert('Votre device a bien été créé');
      loadDevices();
      return data;
    } catch (err: any) {
      if (reserved && admin) {
        // id/label transmis ici aussi : la création a déjà été journalisée côté
        // increment ci-dessus (avant qu'on sache si registerRemoteDevice allait
        // réussir), ce rollback doit donc laisser une trace symétrique plutôt qu'un
        // log "caméra créée" orphelin, sans suppression correspondante.
        const rollbackResult = await decrementAdminDevicesUsed(admin.id, id, label, authMode);
        if (rollbackResult.code === 1) dispatchAdmin(setAdmin({ ...admin, nbDevicesUsed: rollbackResult.payload }));
      }
      showAlert(`Erreur : ${err.message}`, 'important');
    }
  };

  /**
   * Ordonne au device de se couper lui-même (il stoppe sa publication WHIP, libère
   * sa caméra, ferme sa WS et ne retente pas de se reconnecter).
   * Best effort : si la WS de signalisation est fermée ou si le device n'implémente
   * pas `force_disconnect`, c'est le microservice qui coupe la session sur le DELETE.
   */
  const sendForceDisconnect = useCallback((device_id: string) => {
    const ws = signalingWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'force_disconnect', target: device_id }));
    }
  }, [signalingWsRef]);

  /**
   * Devices porteurs du rôle Réalisateur.
   *
   * Le registre vit ici, et non dans le panneau des sources distantes : ce dernier
   * est monté DEUX FOIS (les deux emplacements de la ControlBar) et démonté à la
   * fermeture, deux raisons pour lesquelles un état local divergeait ou se perdait.
   * Trois lecteurs en dépendent — le badge du panneau, la diffusion du deck, et le
   * contrôle d'origine des `director_take`.
   *
   * Le registre est un CACHE, pas la vérité : celle-ci est le drapeau `is_director`
   * porté par la fiche du device côté microservice. Il est reconstruit à chaque
   * `loadDevices`, ce qui fait qu'un rechargement de la régie retrouve les rôles
   * accordés — et surtout qu'un smartphone marqué Réalisateur retrouve sa capacité
   * dès sa reconnexion, sans intervention.
   */
  /**
   * Qualité du lien montant de chaque device, telle qu'IL la mesure.
   *
   * La régie ne peut pas la mesurer elle-même : en mode SFU elle tire le flux
   * depuis MediaMTX, et ce qu'elle observerait décrirait le trajet SFU → régie,
   * c'est-à-dire son propre réseau, pas celui du téléphone.
   *
   * Volatile par nature : rien n'est conservé d'un device déconnecté, son entrée
   * est retirée. Une valeur figée ferait croire à une mesure fraîche.
   */
  const [deviceNetworkQuality, setDeviceNetworkQuality] =
    useState<Map<string, { level: number; sample: any }>>(new Map());

  const handleDeviceNetworkQuality = useCallback((device_id: string, level: number, sample: unknown) => {
    setDeviceNetworkQuality(prev => {
      const next = new Map(prev);
      next.set(device_id, { level, sample });
      return next;
    });
  }, []);

  const handleDeviceNetworkQualityRef = useRef(handleDeviceNetworkQuality);
  useEffect(() => { handleDeviceNetworkQualityRef.current = handleDeviceNetworkQuality; },
    [handleDeviceNetworkQuality]);

  /**
   * Position et direction de visée des appareils, telles qu'ils les rapportent.
   *
   * Volatile pour la même raison que la qualité réseau : l'entrée d'un device
   * déconnecté est retirée. Laisser son dernier point sur la carte laisserait
   * croire qu'un opérateur est encore là où il n'est plus — l'erreur exactement
   * inverse de ce qu'une carte de régie doit servir.
   */
  const [deviceGeo, setDeviceGeo] = useState<Map<string, DeviceGeoReport>>(new Map());

  const handleDeviceGeo = useCallback((device_id: string, report: DeviceGeoReport) => {
    setDeviceGeo(prev => {
      const next = new Map(prev);
      next.set(device_id, report);
      return next;
    });
  }, []);

  const handleDeviceGeoRef = useRef(handleDeviceGeo);
  useEffect(() => { handleDeviceGeoRef.current = handleDeviceGeo; }, [handleDeviceGeo]);

  /**
   * Abonne (ou désabonne) la régie à la position d'appareils donnés.
   *
   * Sans cet appel, un device n'émet sa position que si son porteur a ouvert
   * « Infos » au moins une fois : la carte restait vide sans que rien ne le dise.
   * L'onglet Carte s'abonne à son ouverture et se désabonne à sa fermeture — le
   * suivi coûte la puce GPS allumée sur un téléphone qui encode déjà, on ne
   * l'allume donc que tant que quelqu'un regarde.
   *
   * Le microservice exempte `geo_watch` du filtre « porteur du contrôle » : une
   * régie spectatrice a le droit de voir où sont ses caméras. Best effort, sans
   * accusé — un device qui ne reçoit pas le message reste simplement sans point.
   */
  const setDeviceGeoWatch = useCallback((active: boolean, deviceIds: string[]) => {
    const ws = signalingWsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    deviceIds.forEach(device_id => {
      ws.send(JSON.stringify({ type: 'geo_watch', target: device_id, active }));
    });
  }, [signalingWsRef]);

  /**
   * Allume (ou éteint) la prise de mesures sur des appareils donnés.
   *
   * Le device écrit alors ses relevés de qualité du lien montant dans SON propre
   * fichier — il les poste à l'API de logs d'admin-api, comme n'importe quel log
   * navigateur. Rien ne remonte par la WS de signalisation : elle ne porte que
   * l'ordre. C'est ce qui permet d'avoir un fichier par téléphone sans faire
   * transiter un flux de mesures par le microservice, qui n'en ferait rien.
   *
   * Exempté du filtre « porteur du contrôle » côté microservice, comme
   * `geo_watch` : mesurer n'est pas piloter.
   */
  const setDeviceMetricsLog = useCallback((active: boolean, deviceIds: string[]) => {
    // Retenu pour les appareils qui se connecteront APRÈS le clic : sans ça,
    // l'ordre ne concernait que ceux présents à cet instant. Constaté le
    // 27/08/2026 sur un téléphone au lien instable — il cessait d'écrire ses
    // mesures à sa première reconnexion, et le fichier s'arrêtait au milieu de
    // l'essai sans que rien ne le signale.
    metricsLogActiveRef.current = active;
    const ws = signalingWsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    deviceIds.forEach(device_id => {
      ws.send(JSON.stringify({ type: 'metrics_log', target: device_id, active }));
    });
  }, [signalingWsRef]);

  /** Prise de mesures demandée par l'opérateur, à réaffirmer à chaque connexion. */
  const metricsLogActiveRef = useRef(false);

  const [directorDevices, setDirectorDevices] = useState<Set<string>>(new Set());
  const directorDevicesRef = useRef<Set<string>>(new Set());
  useEffect(() => { directorDevicesRef.current = directorDevices; }, [directorDevices]);

  /**
   * Donne ou retire le rôle Réalisateur à un device (il déverrouille alors son
   * onglet « Réalisateur »).
   *
   * DEUX effets, et il faut les deux : le signal `director` déverrouille l'onglet
   * du device connecté ici et maintenant ; le PATCH inscrit le rôle sur sa fiche
   * pour qu'il soit réappliqué à chaque connexion suivante (cf.
   * `applyDirectorRoleOnConnect`). Sans le PATCH, marquer un device hors ligne
   * n'aurait aucun effet et il repartirait en mode normal.
   *
   * L'affichage est optimiste, la fiche fait foi : si le PATCH échoue, le
   * chargement de devices suivant remet le registre d'aplomb.
   */
  const handleSetDirector = useCallback((device_id: string, active: boolean) => {
    setDirectorDevices(prev => {
      const next = new Set(prev);
      if (active) next.add(device_id);
      else        next.delete(device_id);
      return next;
    });
    // Hors de l'updater : StrictMode rejoue les updaters, et le signal partirait
    // deux fois.
    const ws = signalingWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'director', target: device_id, active }));
    }
    // Promise.resolve().then : requireDeviceStreamPath lève de façon synchrone,
    // et sans cette enveloppe l'exception traverserait le gestionnaire de clic
    // au lieu de tomber dans le .catch juste en dessous.
    Promise.resolve().then(() => updateRemoteDevice(
      settings.serverUrl, requireDeviceStreamPath(), device_id, { is_director: active },
    )).catch(err => {
      console.error('[DIRECTOR] Rôle non enregistré sur la fiche du device:', err);
      showAlert(
        `Le rôle Réalisateur n'a pas pu être enregistré pour « ${device_id} » : il sera perdu `
        + `à la prochaine connexion de cet appareil. (${err.message})`,
        'important',
      );
      loadDevicesRef.current();
    });
  }, [signalingWsRef, settings.serverUrl, requireDeviceStreamPath, showAlert]);

  /**
   * Réarme le rôle Réalisateur d'un device qui vient de se connecter.
   *
   * Appelé depuis `device_connected` / `device_reconnected`, seul moment où l'on
   * sait la WebSocket du device ouverte et routable — le microservice n'émet ces
   * messages qu'après avoir reçu son `ready`.
   *
   * La liste est RELUE plutôt que lue dans le registre local : à cet instant le
   * `loadDevices` déclenché par le même message est encore en vol, et un device
   * qui vient d'être créé n'a jamais figuré dans le registre. Une requête de plus
   * par connexion d'appareil, contre un rôle qui s'applique ou ne s'applique pas.
   */
  const applyDirectorRoleOnConnect = useCallback(async (device_id: string) => {
    const list   = await loadDevicesRef.current();
    const device = list.find(d => d.device_id === device_id);
    if (!device?.is_director) return;

    const ws = signalingWsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) {
      console.warn(`[DIRECTOR] ${device_id} est Réalisateur mais la WS régie est fermée.`);
      return;
    }
    console.log(`[DIRECTOR] 🎬 ${device_id} connecté et marqué Réalisateur → signal director`);
    ws.send(JSON.stringify({ type: 'director', target: device_id, active: true }));
  }, [signalingWsRef]);

  /**
   * Réaffirme la prise de mesures à un appareil qui vient de se connecter.
   *
   * Même raison que le tally réaffirmé à la prise de main : un ordre envoyé une
   * fois ne vaut que pour ceux qui écoutaient. Un téléphone qui se reconnecte
   * repart avec ses mesures éteintes, et son fichier s'arrête au milieu de
   * l'essai — précisément quand le lien se dégrade, donc précisément quand on
   * voulait mesurer.
   */
  const applyMetricsLogOnConnect = useCallback((device_id: string) => {
    if (!metricsLogActiveRef.current) return;
    const ws = signalingWsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'metrics_log', target: device_id, active: true }));
  }, [signalingWsRef]);

  /**
   * Journalise la connexion d'un smartphone dans l'onglet Logs du créateur
   * (`device_connected` / `device_reconnected`). Best-effort : createAdminActionLog
   * n'échoue jamais bruyamment, un log perdu ne doit pas gêner la connexion réelle.
   *
   * Nom de la caméra (pas son device_id), mode de connexion réel (SFU si le
   * serveur a provisionné un path pour cet appareil, sinon Direct), mode
   * d'authentification (Login/Lien) et chaîne. Même gabarit que la création
   * (AdminDeviceIncrement.php).
   */
  const logDeviceConnected = useCallback(async (device_id: string, ip?: string, userAgent?: string) => {
    if (!admin?.id) return;
    const list   = await loadDevicesRef.current().catch(() => [] as RemoteDevice[]);
    const device = list.find(d => d.device_id === device_id);
    const name   = device?.label || device_id;

    const transport = (settings.deviceTransport === 'sfu' && getDeviceSfuInfo(device_id) && !isSfuUnavailable(device_id))
      ? 'SFU' : 'Direct';
    const auth = device?.auth_mode === 'uuid' ? 'Lien' : 'Login';

    let comment = `Caméra : ${name} — Connexion : ${transport} — Authentification : ${auth}`;
    if (channelName) comment += ` — Chaîne : ${channelName}`;
    // IP du téléphone (fournie par le microservice ; absente sur un microservice plus ancien).
    if (ip) comment += ` — IP : ${ip}`;
    // Client du téléphone, déduit du User-Agent (absent sur un microservice plus ancien).
    const client = deviceClientLabel(userAgent);
    if (client) comment += ` — Client : ${client}`;
    createAdminActionLog(admin.id, 'RemoteDeviceConnection', 'Connexion', 'Connected', comment);
  }, [admin?.id, settings.deviceTransport, channelName]);

  /** Symétrique de logDeviceConnected : le nom de la caméra, rien d'autre (le motif `bye` n'est plus journalisé). */
  const logDeviceDisconnected = useCallback(async (device_id: string) => {
    if (!admin?.id) return;
    const list   = await loadDevicesRef.current().catch(() => [] as RemoteDevice[]);
    const name   = list.find(d => d.device_id === device_id)?.label || device_id;
    createAdminActionLog(admin.id, 'RemoteDeviceConnection', 'Deconnexion', 'Disconnected', `Caméra : ${name}`);
  }, [admin?.id]);

  // Même motif que loadDevicesRef / connectToDeviceRef : buildSignalingCallbacks
  // est construit plus haut dans le corps du hook, et une capture directe y
  // figerait la première version du callback.
  const applyMetricsLogOnConnectRef = useRef(applyMetricsLogOnConnect);
  useEffect(() => { applyMetricsLogOnConnectRef.current = applyMetricsLogOnConnect; }, [applyMetricsLogOnConnect]);

  const applyDirectorRoleOnConnectRef = useRef(applyDirectorRoleOnConnect);
  useEffect(() => { applyDirectorRoleOnConnectRef.current = applyDirectorRoleOnConnect; },
    [applyDirectorRoleOnConnect]);

  const logDeviceConnectedRef = useRef(logDeviceConnected);
  useEffect(() => { logDeviceConnectedRef.current = logDeviceConnected; }, [logDeviceConnected]);

  const logDeviceDisconnectedRef = useRef(logDeviceDisconnected);
  useEffect(() => { logDeviceDisconnectedRef.current = logDeviceDisconnected; }, [logDeviceDisconnected]);

  /**
   * Pousse l'état du deck (libellés des sources + celle en final) vers un device
   * Réalisateur, pour alimenter son mini-deck.
   *
   * Best effort et sans mémoire : c'est l'appelant qui décide quand rediffuser.
   * Un device hors ligne au moment de l'envoi ne reçoit rien et gardera un deck
   * périmé jusqu'à la diffusion suivante.
   */
  const sendDirectorDeck = useCallback((
    device_id: string,
    sources: { id: string; label: string; index: number }[],
    finalId: string | null,
  ) => {
    const ws = signalingWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'director_deck', target: device_id, sources, finalId }));
    }
  }, [signalingWsRef]);

  /**
   * Efface toute trace locale d'un device distant qui ne doit plus émettre.
   *
   * Fermer la seule PeerConnection ne suffit pas :
   *  - en mode SFU la session WHEP reste ouverte côté MediaMTX (pas de DELETE) et son
   *    entrée reste dans la map des sessions, ce qui fausse la reconnexion suivante ;
   *  - la source `remote-<id>` garde son emplacement de deck, son emprunt de preview,
   *    son branchement audio et son entrée de registry (d'où le device « qui reste »).
   */
  const teardownRemoteDevice = useCallback((device_id: string) => {
    closeWhepConnection(device_id);
    apiCleanupDeviceBuffers(device_id, { iceCandidateBufferRef, remoteDescSetRef });
    remoteConnectionsRef.current.delete(device_id);
    setRemoteConnections(prev => {
      const next = new Map(prev);
      const conn = next.get(device_id);
      if (conn) { conn.peerConnection.close(); next.delete(device_id); }
      return next;
    });
    remoteDeviceInfoRef.current.delete(device_id);
    remoteDeviceTeardownRef.current(device_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRemoteConnections]);

  /**
   * L'IMAGE est revenue : la place n'est plus tenue, elle est occupée.
   *
   * À n'appeler QUE sur l'arrivée effective d'une piste vidéo — jamais sur
   * `device_connected`. Un appareil qui rouvre sa page se réannonce plusieurs
   * secondes avant que son flux ne reparaisse : lever le drapeau à l'annonce
   * faisait disparaître la source de la liste pendant cet intervalle, l'effet du
   * deck vidait l'emplacement, et comme la source restait marquée « déjà
   * assignée » le remplissage automatique ne la reposait jamais. La caméra
   * revenait donc sans vignette, et il fallait l'engrenage. C'était le défaut du
   * 2026-08-31.
   */
  const clearDeviceAwaiting = useCallback((device_id: string) => {
    holdRemoteDeckSlot(device_id);
    const timer = awaitingTimersRef.current.get(device_id);
    if (timer) { clearTimeout(timer); awaitingTimersRef.current.delete(device_id); }
    resumeDeviceNegotiation(device_id);
    awaitingDevicesRef.current.delete(device_id);
    setAwaitingDevices(prev => {
      if (!prev.has(device_id)) return prev;
      const next = new Set(prev);
      next.delete(device_id);
      return next;
    });
  }, [holdRemoteDeckSlot]);

  /**
   * L'appareil se réannonce : la place reste tenue, mais le compte à rebours
   * repart et la négociation redevient permise.
   *
   * Ne crée pas de place pour un appareil qui n'en avait pas — une première
   * connexion suit son chemin ordinaire.
   */
  const renewDeviceAwaiting = useCallback((device_id: string) => {
    resumeDeviceNegotiation(device_id);
    holdRemoteDeckSlot(device_id);
    if (!awaitingDevicesRef.current.has(device_id)) return;
    markDeviceAwaitingRef.current(device_id);
  }, [holdRemoteDeckSlot]);

  /**
   * L'appareil est tombé : on tient sa place pendant le délai de grâce.
   *
   * Le `console.warn` de l'expiration est délibéré — c'est le seul moment où la
   * régie constate qu'une caméra ne reviendra pas, et le journal distant ne remonte
   * que `warn` et `error`.
   */
  const markDeviceAwaiting = useCallback((device_id: string) => {
    const existing = awaitingTimersRef.current.get(device_id);
    if (existing) clearTimeout(existing);
    resumeDeviceNegotiation(device_id);
    holdRemoteDeckSlot(device_id);

    awaitingTimersRef.current.set(device_id, setTimeout(() => {
      awaitingTimersRef.current.delete(device_id);
      awaitingDevicesRef.current.delete(device_id);

      // Ceinture : si l'appareil a retrouvé une connexion vivante sans que
      // `clearDeviceAwaiting` ait été appelé — le P2P, par exemple, dont la piste
      // n'emprunte pas le même chemin — le démonter ici couperait une caméra qui
      // marche. On se contente alors de ranger le drapeau.
      if (remoteConnectionsRef.current.has(device_id)) {
        setAwaitingDevices(prev => {
          if (!prev.has(device_id)) return prev;
          const next = new Set(prev);
          next.delete(device_id);
          return next;
        });
        return;
      }

      console.warn(
        `[DEVICES] ⌛ ${device_id} absent depuis ${AWAITING_GRACE_MS / 60_000} min — `
        + `emplacement de deck libéré, négociation abandonnée`,
      );
      // Dans cet ordre : couper la relance AVANT de démonter, sinon le démontage
      // peut lui-même déclencher une dernière tentative.
      abandonDeviceNegotiation(device_id);
      teardownRemoteDevice(device_id);
      releaseRemoteDeckSlot(device_id);
      setAwaitingDevices(prev => {
        if (!prev.has(device_id)) return prev;
        const next = new Set(prev);
        next.delete(device_id);
        return next;
      });
    }, AWAITING_GRACE_MS));

    awaitingDevicesRef.current.add(device_id);
    setAwaitingDevices(prev => prev.has(device_id) ? prev : new Set(prev).add(device_id));
  }, [teardownRemoteDevice, releaseRemoteDeckSlot, holdRemoteDeckSlot, remoteConnectionsRef]);

  useEffect(() => { markDeviceAwaitingRef.current  = markDeviceAwaiting;  }, [markDeviceAwaiting]);
  useEffect(() => { renewDeviceAwaitingRef.current = renewDeviceAwaiting; }, [renewDeviceAwaiting]);
  useEffect(() => { clearDeviceAwaitingRef.current = clearDeviceAwaiting; }, [clearDeviceAwaiting]);

  // Les minuteries survivraient au démontage de la régie et rappelleraient des
  // setState sur un composant disparu.
  useEffect(() => () => {
    awaitingTimersRef.current.forEach(t => clearTimeout(t));
    awaitingTimersRef.current.clear();
  }, []);

  /** Suppression SANS confirmation — pour les opérations de masse (réinitialisation de la régie). */
  const deleteDeviceNow = async (device_id: string) => {
    {
      // Capturé avant le DELETE : une fois le device supprimé/déconnecté, son label et
      // son authMode disparaissent de remoteDevices et ne seraient plus récupérables
      // pour le log.
      const deletedDevice = remoteDevicesRef.current.find(d => d.device_id === device_id);
      const label    = deletedDevice?.label;
      const authMode = deletedDevice?.auth_mode ?? 'password';

      // Couper le device AVANT de supprimer ses identifiants : le DELETE ne fait que
      // révoquer les credentials, un device déjà connecté continuerait à publier sans
      // plus aucun moyen de se réauthentifier (session fantôme jusqu'au redémarrage).
      sendForceDisconnect(device_id);
      try {
        await deleteRemoteDevice(settings.serverUrl, requireDeviceStreamPath(), device_id);
        if (admin) {
          const result = await decrementAdminDevicesUsed(admin.id, device_id, label, authMode);
          if (result.code === 1) dispatchAdmin(setAdmin({ ...admin, nbDevicesUsed: result.payload }));
        }
      } catch (err) { console.error('Erreur suppression:', err); }
      // Nettoyage local inconditionnel : le device vient d'être déconnecté de force,
      // il ne doit rien laisser derrière lui même si le DELETE a échoué.
      teardownRemoteDevice(device_id);
      clearDeviceSfuInfo(device_id);
      releaseRemoteDeckSlot(device_id);
      loadDevices();
    }
  };

  const handleDeleteDevice = (device_id: string) => {
    showConfirm(`Supprimer le device ${device_id} ?`, () => deleteDeviceNow(device_id));
  };

  const handleDisconnectDevice = (device_id: string) => {
    showConfirm(`Déconnecter le device ${device_id} de la régie ?`, async () => {
      sendForceDisconnect(device_id);
      // Le device reste enregistré et peut se reconnecter : on ne touche ni à son
      // info SFU (réémise à la reconnexion) ni à son historique d'assignation de slot.
      teardownRemoteDevice(device_id);
      loadDevices();
    });
  };

  const handleRegeneratePassword = async (device_id: string) => {
    try {
      const data = await regenerateDevicePassword(settings.serverUrl, requireDeviceStreamPath(), device_id);
      showAlert(`Nouveau mot de passe : ${data.new_password}`);
      loadDevices();
    } catch (err) { console.error('Erreur régénération:', err); }
  };


  const connectToSignalingServer = useCallback(() => {
    if (!channelActiveRef.current) {
      console.log('[SIGNALING] Chaîne inactive — connexion WS annulée');
      return () => {};
    }
    // SANS repli, pour la même raison que la liste des smartphones : le canal de
    // contrôle est celui du microservice de LA chaîne. Retomber sur
    // settings.streamPath faisait ouvrir la signalisation d'une AUTRE chaîne —
    // celle dont l'UUID est en dur dans DEFAULT_SETTINGS — et sur `device_list` la
    // régie ne se contente pas d'afficher : elle NÉGOCIE avec chaque appareil. Une
    // caméra d'une autre chaîne devenait donc une vignette vivante, chargeable à
    // l'antenne. Constaté en production le 16/08/2026.
    if (!deviceStreamPath) {
      console.warn('[SIGNALING] Aucune chaîne identifiée — connexion WS annulée');
      return () => {};
    }
    const signalingUrl = buildSignalingUrl(settings.serverUrl, deviceStreamPath, observerMode === 'silent');
    const config: SignalingConfig = { serverUrl: settings.serverUrl, streamPath: deviceStreamPath, signalingUrl };
    return apiConnectToSignalingServer(
      config, { wsRef: signalingWsRef, wsReconnectTimeoutRef, wsHbIntervalRef, wsPongTimeoutRef }, { addSdpLog: addSdpLogRegie },
      // ── v2.45 : utilise handleSignalingMessageRef pour permettre la surcharge par LiveStreamManager ──
      (msg) => handleSignalingMessageRef.current(msg),
      () => {
        if (channelActiveRef.current) {
          connectToSignalingServerRef.current();
          return;
        }
        // Chaîne désactivée (toggle individuel ou app:broadcasting:shutdown-all côté
        // serveur — dans ce dernier cas rien ne prévient jamais le navigateur, seule
        // la fermeture du WS de signalisation le révèle) : le serveur de signalisation
        // n'existe plus (container détruit avec le channel), donc plus aucun device
        // n'est réellement connecté. Sans ce nettoyage, remoteConnections/remoteDevices
        // gardaient leur dernière valeur connue indéfiniment — le nombre de devices
        // affiché restait celui d'avant l'arrêt de la chaîne.
        console.log('[SIGNALING] Chaîne inactive — reconnexion WS annulée, purge des devices connectés');
        setRemoteConnections(() => new Map());
        setRemoteDevices([]);
      }
    );
  }, [settings.serverUrl, deviceStreamPath, observerMode, addSdpLogRegie, setRemoteConnections, wsPongTimeoutRef]);

  useEffect(() => { connectToSignalingServerRef.current = connectToSignalingServer; }, [connectToSignalingServer]);

  /**
   * Prête la WebSocket de contrôle au contexte, qui en a besoin pour prévenir les
   * smartphones quand la page se ferme — le seul moment où l'on ne peut plus rien
   * ouvrir. Cf. announceDepartureBeforeUnload dans MediaStreamContext.
   */
  useEffect(() => {
    registerControlSocket(signalingWsRef);
    return () => registerControlSocket(null);
  }, [registerControlSocket, signalingWsRef]);

  const disconnectSignaling = useCallback(() => {
    apiDisconnectSignaling({ wsRef: signalingWsRef, wsReconnectTimeoutRef, wsHbIntervalRef, wsPongTimeoutRef });
  }, [wsReconnectTimeoutRef, wsHbIntervalRef, wsPongTimeoutRef]);


  // ── v2.45 : initialise le ref avec l'impl par défaut, surchargeable par LiveStreamManager ──
  // useEffect(() => {
    // handleSignalingMessageRef.current = handleSignalingMessageImpl;
  // }, [handleSignalingMessageImpl]);

  const connectToDeviceImpl = useCallback(async (device_id: string) => {
    // ── Mode SFU : la régie tire le flux du path source MediaMTX en WHEP.
    // Même injectStream, même comptabilité remoteConnections — seul le
    // transport change. Le retour programme est lu par le device en WHEP
    // (program_whep_url reçu dans auth_success), pas de PC retour P2P.
    //
    // Garde-fou : ne prendre cette branche que si elle est praticable.
    // Le bloc `sfu` (path + URLs WHIP/WHEP) est provisionné par le microservice
    // et transmis dans device_connected / device_reconnected. Son absence veut
    // dire que le SFU n'existe pas pour ce device : SFU_TRANSPORT_ENABLED=false
    // sur la chaîne, ou mediamtx-manager injoignable au moment de l'auth.
    //
    // Reconstruire l'URL WHEP à la main dans ce cas (ce que faisait le `??`
    // buildSourceWhepUrl) fait tirer la régie sur un path MediaMTX inexistant.
    // Le vrai dégât n'est pas le WHEP raté, c'est le `return` qui suit : la
    // régie n'émet JAMAIS l'offre P2P, le device n'a rien reçu non plus (pas de
    // bloc sfu → il reste en P2P) et attend une offre qui ne viendra pas. Les
    // deux bouts s'attendent, seul l'intercom — qui ne passe pas par ici —
    // continue de fonctionner.
    //
    // Constaté en production le 14/08/2026 sur Lovelight TV : device authentifié,
    // SDP toutes en `vidéo:aucun`, appareil bloqué sur « en attente ».
    if (settings.deviceTransport === 'sfu') {
      const sfu = getDeviceSfuInfo(device_id);
      if (sfu && !isSfuUnavailable(device_id)) {
        await apiConnectToDeviceWhep(device_id, sfu.whep_url, buildSignalingRefs(), buildSignalingCallbacks());

        // ── Le RETOUR : une question séparée du montant ──────────────────────
        //
        // En 'mediamtx', on ne pousse RIEN : le device tire le programme depuis le
        // serveur, que la régie publie une seule fois pour tout le parc. C'est ce
        // qui passe à l'échelle — pousser en direct, c'est une PeerConnection, un
        // encodeur et un débit montant PAR téléphone.
        //
        // En 'p2p', on pousse comme avant. Plus cher, mais le retour existe dès que
        // la régie est ouverte, là où la voie serveur dépend de la diffusion.
        //
        // Le device n'a pas besoin qu'on lui annonce le choix : il tente le serveur,
        // et c'est l'arrivée — ou non — de cette Offer qui le lui dit. S'il ne
        // trouve rien, il demande le direct par `return_refresh`, honoré quel que
        // soit le réglage : c'est le repli, et il n'a pas à être désactivable.
        if (settings.returnTransport === 'p2p') {
          setTimeout(async () => {
            await sendReturnStreamToDevice(
              device_id,
              buildSignalingRefs(),
              buildSignalingCallbacks(),
            );
          }, 500);
        }
        return;
      }
      // Repli en P2P — et on le dit, une fois par device : sans ça, l'opérateur
      // voit un appareil « connecté » qui ne renvoie jamais d'image, sans
      // aucune trace de la raison.
      const reason = sfu
        ? 'le flux SFU de cet appareil est resté injoignable'
        : "le serveur n'a pas provisionné de path SFU pour cet appareil";
      addSdpLogRegie({
        direction: 'info', type: 'event',
        label: `⚠ Transport SFU indisponible (${reason}) — repli en direct (P2P)`,
        deviceId: device_id,
      });
      if (!sfuFallbackNotifiedRef.current.has(device_id)) {
        sfuFallbackNotifiedRef.current.add(device_id);
        showAlert(
          `« Connexion sécurisée » indisponible pour ${device_id} : ${reason}. `
          + 'La régie bascule en transport direct (P2P) pour cet appareil.',
          'important',
        );
      }
    }

    await apiConnectToDevice(
      device_id,
      buildSignalingRefs(),
      buildSignalingCallbacks(),
      (id) => connectToDeviceRef.current(id),
    );
    setTimeout(async () => {
      await sendReturnStreamToDevice(
        device_id,
        buildSignalingRefs(),
        buildSignalingCallbacks(),
      );
    }, 500);
  }, [buildSignalingRefs, buildSignalingCallbacks, settings.deviceTransport, settings.returnTransport, addSdpLogRegie, showAlert]);

  useEffect(() => { connectToDeviceRef.current = connectToDeviceImpl; }, [connectToDeviceImpl]);

  // ── Reprise des flux retour en attente ─────────────────────────────────────
  //
  // Le retour n'est tenté qu'une fois, 500 ms après la connexion au device, et
  // il échoue tant que le canvas de la régie n'existe pas. Ce ticker rejoue les
  // devices retenus par api_webrtcPeerConnection dès que la cause disparaît.
  //
  // Il tourne en continu plutôt que d'être démarré à la demande : la file vit
  // dans le module de signalisation, pas dans un état React, et
  // retryPendingReturnStreams sort sur un test de taille quand elle est vide —
  // ce qui est le cas la quasi-totalité du temps.
  //
  // Les constructeurs passent par un ref : ils changent d'identité à chaque
  // rendu, et les mettre en dépendance recréerait l'intervalle assez souvent
  // pour qu'il ne se déclenche jamais.
  const returnRetryDepsRef = useRef({ buildSignalingRefs, buildSignalingCallbacks });
  returnRetryDepsRef.current = { buildSignalingRefs, buildSignalingCallbacks };
  useEffect(() => {
    const id = setInterval(() => {
      if (!hasPendingReturnStreams()) return;
      const { buildSignalingRefs: mkRefs, buildSignalingCallbacks: mkCbs } = returnRetryDepsRef.current;
      retryPendingReturnStreams(mkRefs(), mkCbs()).then(restored => {
        if (restored > 0) console.log(`[PC-RETURN] ↻ ${restored} flux retour rétabli(s) après attente du canvas`);
      });
    }, RETURN_RETRY_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);




  const probeWhipCodec = useCallback(async (retryDeadline?: number) => {
    if (requiredCodecRef.current) { setCodecProbeState('done'); return; }
    if (!effectiveStreamPath) { setCodecProbeState('idle'); return; }
    // Même course que startWebRTCStreamingImpl (LiveStreamManager) : cette sonde publie
    // elle aussi pour de vrai sur la chaîne dès que son adresse est résolue, donc dès le
    // montage — bien avant que channelStatus n'atteigne 'active' après une activation. Sans
    // cette attente elle tombait sur 410 en silence (console seulement) et ne réessayait
    // JAMAIS, faute de retentative : l'effet qui l'appelle ne se redéclenche que si
    // `effectiveStreamPath` change d'identité, ce qui n'arrive pas pour la même chaîne.
    if (channelStatus === 'activating' || channelStatus === 'activating_restricted') {
      const deadline = retryDeadline ?? (Date.now() + 20_000);
      if (Date.now() >= deadline) { console.warn('[WHIP] Sonde de codec abandonnée : chaîne toujours en activation après 20 s'); setCodecProbeState('error'); return; }
      setTimeout(() => { probeWhipCodecRef.current(deadline); }, 1000);
      return;
    }
    // Chaîne franchement éteinte (pas en cours d'activation) : le WHIP répondra 410 à coup
    // sûr, inutile de le tenter. Constaté le 15/09/2026 — un rechargement de page pile entre
    // une désactivation et sa réactivation (fenêtre de quelques dizaines de secondes) faisait
    // partir la sonde sur une chaîne réellement inactive, échec loggé sans retentative. On
    // ne boucle pas ici en retentant toutes les secondes comme ci-dessus (rien n'indique
    // qu'une activation est en cours) : on attend simplement que `channelStatus` change,
    // ce que l'effet ci-dessous guette désormais.
    if (channelStatus === 'inactive' || channelStatus === 'deactivating' || channelStatus === 'deactivating_restricted' || channelStatus === 'error') {
      setCodecProbeState('idle');
      return;
    }
    setCodecProbeState('probing');
    const whipUrl = `${settings.serverUrl}/${effectiveStreamPath}/webrtc/whip`;
    try {
      // Même profil que la vraie publication (api_whip) : sinon la sonde négocie le
      // barème Baseline et l'impose aux devices jusqu'au premier On air.
      const profilePrefix = whipProfilePrefix(getActiveWhipEncoding().videoProfile);
      const result = await apiProbeWhipCodec(whipUrl, (sdp) => forceSdpCodecs(sdp, profilePrefix));
      setNegotiatedWhipCodec(buildNegotiatedCodec(result.codec, result.profile));
      requiredCodecRef.current = { codec: result.codec, profile: result.profile };
      setCodecProbeState('done');
    } catch (err: any) {
      console.error('❌ Erreur négo WHIP:', err.message);
      setCodecProbeState('error');
    }
  }, [settings.serverUrl, effectiveStreamPath, channelStatus, setNegotiatedWhipCodec]);

  /**
   * La sonde de codec WHIP publie réellement sur la chaîne : elle attend donc de
   * savoir laquelle. Lancée au montage seulement, elle était sautée pour de bon
   * quand l'UUID n'était pas encore résolu — et la publication suivante partait
   * sans codec négocié.
   *
   * ⚠️ La dépendance est le CHEMIN DE LA CHAÎNE, jamais la fonction. `probeWhipCodec`
   * dépend de `setNegotiatedWhipCodec`, que le contexte recrée à chaque rendu : la
   * prendre en dépendance relançait une sonde à CHAQUE rendu — une publication WHIP
   * par rendu, jusqu'à figer l'application entière. Constaté en production le
   * 16/08/2026.
   */
  const probeWhipCodecRef = useRef(probeWhipCodec);
  useEffect(() => { probeWhipCodecRef.current = probeWhipCodec; });

  // `channelStatus` en dépendance : sans lui, une sonde abandonnée en 'idle'/'error' faute
  // de chaîne active ne reprenait jamais quand la chaîne s'activait ensuite — l'effet ne se
  // redéclenchait que si `effectiveStreamPath` changeait d'identité, ce qui n'arrive pas pour
  // la même chaîne.
  useEffect(() => { probeWhipCodecRef.current(); }, [effectiveStreamPath, channelStatus]);

  /**
   * Amorce de permission, à faire AVANT toute enumerateDevices().
   *
   * `enumerateDevices()` ne révèle les `videoinput` que si la permission caméra a
   * déjà été accordée : sans elle le navigateur renvoie des entrées au `deviceId`
   * et au `label` vides, et la webcam locale n'apparaît jamais dans les sources.
   * La régie ne demandait que le micro (mic intercomm, LiveStreamManager) — d'où
   * une liste de caméras anonyme, donc inutilisable. Constaté le 22/08/2026.
   * Le device fait cette amorce depuis toujours, cf. `api_deviceMedia.ts`.
   *
   * Le flux est ouvert puis relâché aussitôt : il ne sert qu'à débloquer les
   * étiquettes. Une seule fois par montage — `getLocalDevices` est aussi le
   * gestionnaire de `devicechange`, et rouvrir la caméra à chaque branchement de
   * périphérique rallumerait la diode pour rien.
   */
  const permissionPrimedRef = useRef(false);

  const primeDevicePermissions = useCallback(async () => {
    if (permissionPrimedRef.current) return;
    permissionPrimedRef.current = true;

    // audio+video d'abord : une seule invite couvre les deux listes. Repli sur la
    // vidéo seule si le micro est refusé ou absent — sinon un micro indisponible
    // suffirait à laisser les caméras anonymes.
    for (const constraints of [{ video: true, audio: true }, { video: true }]) {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia(constraints);
        tmp.getTracks().forEach(t => t.stop());
        return;
      } catch (err) {
        console.warn(
          `[DEVICES] Amorce de permission refusée pour ${JSON.stringify(constraints)} :`,
          (err as any)?.name ?? err,
        );
      }
    }
  }, []);

  const getLocalDevices = useCallback(async () => {
    try {
      await primeDevicePermissions();

      const list  = await navigator.mediaDevices.enumerateDevices();
      const video = list.filter(d => d.kind === 'videoinput');
      const audio = list.filter(d => d.kind === 'audioinput');

      // Un deviceId vide signe une permission non accordée : la source serait
      // injouable de toute façon (`DeviceAcquirer` demande `deviceId: { exact }`).
      // Le dire ici, sinon « la régie ne voit pas ma webcam » ne laisse aucune
      // trace dans le journal distant, qui ne remonte que warn et error.
      const anonymes = video.filter(d => !d.deviceId).length;
      if (anonymes > 0)
        console.warn(`[DEVICES] ${anonymes} caméra(s) sans deviceId — permission caméra refusée ou bloquée par la politique du navigateur.`);

      console.warn(`[DEVICES] ${video.length} caméra(s) · ${audio.length} micro(s) énumérés.`);

      setDevices({ video, audio });
      if (audio.length > 0 && !selectedAudio) setSelectedAudio(audio[0].deviceId);
    } catch (err) { console.error('Erreur devices (énumération locale):', err); }
  }, [primeDevicePermissions, selectedAudio, setSelectedAudio]);

  const acquireExternalUrlStream = useCallback((url: string) => apiAcquireExternalUrlStream(url), []);

  const updateVideoSources = useCallback((
    videoDevices  : MediaDeviceInfo[],
    connections   : Map<string, RemoteConnection>,
    remoteDev     : RemoteDevice[],
    extraSources  : VideoSource[] = [],
  ) => {
    const sources: VideoSource[] = [];

    videoDevices.forEach(d => sources.push({
      id: d.deviceId, label: d.label || `Caméra ${d.deviceId.slice(0, 8)}`, type: 'device', deviceId: d.deviceId,
    }));

    connections.forEach((conn, device_id) => {
      const device = remoteDev.find(d => d.device_id === device_id);
      sources.push({ id: `remote-${device_id}`, label: `📱 ${device?.label ?? device_id}`, type: 'remote', stream: conn.stream ?? undefined });
    });

    // Un appareil tombé mais encore attendu garde une source, donc son emplacement.
    // Sans flux : la mini-vue affichera « en attente » plutôt que du noir, et
    // n'essaiera pas de l'emprunter. Le garde `connections.has` évite le doublon
    // pendant le battement où la reconnexion a abouti sans que la minuterie ait
    // encore été annulée.
    awaitingDevices.forEach(device_id => {
      if (connections.has(device_id)) return;
      const device = remoteDev.find(d => d.device_id === device_id);
      sources.push({
        id       : `remote-${device_id}`,
        label    : `📱 ${device?.label ?? device_id}`,
        type     : 'remote',
        awaiting : true,
      });
    });

    externalSources.forEach(ext => sources.push({
      id: `url-${ext.id}`, label: `🌐 ${ext.label || ext.url}`, type: 'url', externalUrl: ext.url,
    }));

    // ── screenActive marque les copies d'écran effectivement capturées : reste utilisé
    // pour masquer les emplacements non configurés des listes de sélection du deck, sans
    // retirer la source de `videoSources` (nécessaire pour relancer une capture via
    // "Choisir une source", qui recherche l'entrée par id dans cette même liste) ──
    screenShareSlots.forEach(slot => {
      const stream = screenShareStreams.get(slot.id);
      const screenActive = !!(stream?.active && stream.getVideoTracks()[0]?.readyState === 'live');
      sources.push({
        id: slot.id,
        label: `🖥️ ${slot.label}`,
        type: 'screen',
        screenActive,
        screenConfig: {
          width: slot.config.width,
          height: slot.config.height,
          frameRate: slot.config.frameRate,
          cursor: slot.config.cursor,
          audio: slot.config.audio,
        },
      });
    });

    const hlsVideoUrl = effectiveStreamPath ? `${settings.serverUrl}/video_${effectiveStreamPath}/index.m3u8` : null;
    const hlsLiveUrl  = effectiveStreamPath ? `${settings.serverUrl}/live_${effectiveStreamPath}/index.m3u8`  : null;
    // Flux HLS de la chaîne elle-même : « Playlist » (video_UUID) et « OBS, RTMP... »
    // (live_UUID). Chaîne arrêtée = ni container ni path MediaMtx : ces flux n'existent
    // pas, on ne propose donc pas les sources. Elles reviennent au redémarrage
    // (resetDeckInit remet le deck à plat sur chaque bascule de channelActive, et
    // buildDefaultVideoRow réattribue sa ligne à la Playlist).
    if (channelActive) {
      if (effectiveStreamPath && hlsVideoUrl && !sources.find(s => s.externalUrl?.includes(`video_${effectiveStreamPath}`)))
        sources.push({ id: `hls-video-${effectiveStreamPath}`, label: 'Playlist', type: 'url', externalUrl: hlsVideoUrl });
      if (effectiveStreamPath && hlsLiveUrl && !sources.find(s => s.externalUrl?.includes(`live_${effectiveStreamPath}`)))
        sources.push({ id: `hls-live-${effectiveStreamPath}`, label: 'OBS, RTMP...', type: 'url', externalUrl: hlsLiveUrl });
    }

    extraSources.forEach(extra => {
      if (!sources.find(s => s.id === extra.id)) sources.push(extra);
    });

    audioOnlySources.forEach(a => { if (!sources.find(s => s.id === a.id)) sources.push(a); });

    // ── v2.42 : préserver les sources URL customs (HLS ajoutés manuellement) ──
    // ── v2.47 : bail-out d'égalité (cf. sameVideoSourceList) ──
    setVideoSources(prev => {
      const builtIds = new Set(sources.map(s => s.id));
      const customs  = prev.filter(s => s.type === 'url' && !builtIds.has(s.id)
        && !s.id.startsWith('hls-video-') && !s.id.startsWith('hls-live-'));
      // Concaténation plutôt que `sources.push` : l'updater ne doit pas muter le
      // tableau de la closure, sous peine de doubler les customs quand React
      // rejoue l'updater (StrictMode).
      const next = customs.length ? [...sources, ...customs] : sources;

      if (!selectedVideoSource) {
        const first = next.find(s => s.type !== 'screen');
        if (first) setSelectedVideoSource(first.id);
      }

      // Liste identique au champ près : renvoyer `prev` préserve l'identité de
      // `videoSources`, ce qui coupe la cascade de rendus en aval (recalcul de
      // deckSlots, effets du deck, du multiview et de la preview).
      if (sameVideoSourceList(prev, next)) return prev;

      // Journalisé seulement sur changement réel : cette fonction est appelée
      // plusieurs fois par seconde pendant une négociation WebRTC, et la console
      // ouverte est elle-même un facteur de blocage.
      console.log('[updateVideoSources] sources construites=', next.map(s => s.id.slice(0, 8)).join(','));
      return next;
    });
  }, [selectedVideoSource, setSelectedVideoSource, setVideoSources, externalSources, settings.serverUrl, effectiveStreamPath, screenShareSlots, screenShareStreams, channelActive, awaitingDevices, audioOnlySources]);

  // ── startPreviewForSource : utilise registry.borrow() + stocke le handle ──
  const startPreviewForSource = useCallback(async (source: VideoSource) => {
    const existing = previewStreamsRef.current.get(source.id);
    if (existing?.active) return;

    // Nettoyer le handle zombie si présent et stream mort (après forceRelease)
    const existingHandle = previewBorrowHandlesRef.current.get(source.id);
    if (existingHandle) {
      const existingStream = previewStreamsRef.current.get(source.id);
      if (existingStream?.active) return;
      // Stream mort — nettoyer le handle pour permettre la réacquisition
      try { existingHandle.release(); } catch {}
      previewBorrowHandlesRef.current.delete(source.id);
    }

    try {
      let stream: MediaStream | null = null;

      if (source.type === 'device' || source.type === 'remote') {
        const handle = await registry.borrow(source.id);
        stream = handle.stream;
        previewBorrowHandlesRef.current.set(source.id, handle);

      } else if (source.type === 'url') {
        try {
          const handle = await registry.borrow(source.id);
          stream = handle.stream;
          previewBorrowHandlesRef.current.set(source.id, handle);
        } catch {
          // Flux pas encore disponible — sera retenté via hlsProber.statusMap
        }

      } else if (source.type === 'screen') {
        const regState = registry.getState(source.id);
        if (regState === 'active') {
          const handle = await registry.borrow(source.id);
          stream = handle.stream;
          previewBorrowHandlesRef.current.set(source.id, handle);
        }

      } else if (source.type === 'scene' && source.stream) {
        stream = source.stream;
      }

      if (stream) {
        previewStreamsRef.current.set(source.id, stream);
        setPreviewStreams(new Map(previewStreamsRef.current));
      }
    } catch (e) {
      console.warn('[startPreviewForSource] acquisition échouée pour', source.id, e);
    }
  }, [registry]);

  // ── stopPreviewForSource : release le handle stocké ──────────────────────
  const stopPreviewForSource = useCallback((sourceId: string) => {
    const handle = previewBorrowHandlesRef.current.get(sourceId);
    if (handle) {
      try { handle.release(); } catch {}
      previewBorrowHandlesRef.current.delete(sourceId);
    }
    previewStreamsRef.current.delete(sourceId);
    setPreviewStreams(new Map(previewStreamsRef.current));
  }, []);

  const handleDeckPreview = useCallback(async (source: VideoSource) => {
    if (deckPreviewSourceIdRef.current === source.id) return;
    deckPreviewSourceIdRef.current = source.id;
    setDeckPreviewSourceId(source.id);

    if (source.type === 'scene' && source.stream) {
      if (deckPreviewVideoRef.current) {
        deckPreviewVideoRef.current.srcObject = source.stream;
        deckPreviewVideoRef.current.play().catch(() => {});
      }
      previewStreamsRef.current.set(source.id, source.stream);
      setPreviewStreams(new Map(previewStreamsRef.current));
      return;
    }

    if (!previewStreamsRef.current.has(source.id)) await startPreviewForSource(source);
    const stream = previewStreamsRef.current.get(source.id);
    if (stream && deckPreviewVideoRef.current) {
      deckPreviewVideoRef.current.srcObject = stream;
      deckPreviewVideoRef.current.play().catch(() => {});
    }
  }, [startPreviewForSource]);

  const handleDeckPreviewRef = useRef(handleDeckPreview);
  useEffect(() => { handleDeckPreviewRef.current = handleDeckPreview; }, [handleDeckPreview]);

  // Les raccourcis clavier sont montés une fois pour toutes (effet à dépendances vides) :
  // appeler `onHandleDeckSwitch` en direct y figeait la version du premier rendu, et avec
  // elle tout ce qu'elle voyait alors — le Switch audio sur « Source », le micro choisi au
  // chargement… Une source passée à l'antenne au clavier rebranchait donc le mixer sur
  // elle-même alors que le Switch restait sur « Fixe ». Même parade que pour le cut.
  const onHandleDeckSwitchRef = useRef(onHandleDeckSwitch);
  useEffect(() => { onHandleDeckSwitchRef.current = onHandleDeckSwitch; }, [onHandleDeckSwitch]);

  // ── CORRECTIF PRINCIPAL : filtre auto-fill ────────────────────────────────
  const isAutoFillable = useCallback((src: VideoSource): boolean => {
    if (src.type === 'scene')  return false;
    if (src.type === 'screen') return false;
    if (src.type === 'audio')  return false;   // n'existe que posée par l'opérateur
    if (src.externalUrl && src.externalUrl.includes(`live_${effectiveStreamPathRef.current}`)) return false;
    return true;
  }, []);

  // Replace les sources/scènes à l'emplacement mémorisé pour cette chaîne.
  // Modifie `slots` en place ; les scènes passent par l'état et la doublure.
  const placeRememberedSources = (slots: (VideoSource | null)[]): boolean => {
    const layoutsNext = [...deckSlotLayoutsRef.current];
    const r = applyDeckEntries(deckEntriesRef.current, slots, layoutsNext, videoSources, allLayoutSlots);
    if (r.layoutsChanged) {
      deckSlotLayoutsRef.current = layoutsNext;
      setDeckSlotLayouts(layoutsNext);
    }
    slots.forEach(s => { if (s) everAssignedToSlotRef.current.add(s.id); });
    return r.slotsChanged || r.layoutsChanged;
  };

  useEffect(() => {
    if (!videoSources.length) return;

    const currentIds = videoSources.map(s => s.id).sort().join(',');

    if (deckInitDone.current && currentIds === lastSourceIdsRef.current) {
      setDeckSlots(prev => {
        let anyChange = false;
        const next = prev.map(slot => {
          if (!slot) return null;
          const fresh = videoSources.find(s => s.id === slot.id);
          if (!fresh) { anyChange = true; return null; }
          if (fresh !== slot) { anyChange = true; return fresh; }
          return slot;
        });

        // Retour d'une source à sa place mémorisée, avant tout remplissage automatique
        if (placeRememberedSources(next)) anyChange = true;

        // Auto-fill pour les sources jamais assignées (ex: remote reconnecté)
        videoSources
          .filter(src => isAutoFillable(src) && !everAssignedToSlotRef.current.has(src.id))
          .forEach(src => {
            if (!next.some(s => s?.id === src.id)) {
              const idx = next.findIndex((s, i) => s === null && !deckSlotLayoutsRef.current[i] && !deckEntriesRef.current[i]);
              if (idx !== -1) {
                next[idx] = src;
                anyChange = true;
                everAssignedToSlotRef.current.add(src.id);
              }
            }
          });

        if (!anyChange) return prev;
        deckSlotsRef.current = next;
        saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
        return next;
      });
      return;
    }
    lastSourceIdsRef.current = currentIds;

    if (!deckInitDone.current) {
      deckInitDone.current = true;

      // Mémoire de cette chaîne : si elle existe, elle commande le placement ; le
      // remplissage par défaut ne sert plus qu'aux sources qu'elle ne connaît pas.
      const remembered = loadDeckEntries(deckChannelUuidRef.current);
      deckEntriesRef.current   = remembered ?? Array(SLOT_COUNT).fill(null);
      deckEntriesReady.current = true;
      const hasMemory = !!remembered?.some(Boolean);
      if (!hasMemory) markDeckAuto(deckChannelUuidRef.current, true);

      const rows = hasMemory ? [] : buildDefaultVideoRow(videoSources, effectiveStreamPathRef.current);
      const nextVideo: (VideoSource | null)[] = [];
      for (let ri = 0; ri < SLOT_COUNT; ri++) {
        nextVideo.push(ri < rows.length ? rows[ri].slot : null);
      }
      const nextLayouts: (LayoutSlot | null)[] = Array(SLOT_COUNT).fill(null);

      if (hasMemory) {
        applyDeckEntries(deckEntriesRef.current, nextVideo, nextLayouts, videoSources, allLayoutSlots);
      }

      // Sources déjà vues (mémorisées) : jamais reposées d'office sur un bouton.
      loadKnownSources(deckChannelUuidRef.current).forEach(id => everAssignedToSlotRef.current.add(id));

      videoSources
        .filter(src => isAutoFillable(src) && !everAssignedToSlotRef.current.has(src.id))
        .forEach(src => {
          if (!nextVideo.some(s => s?.id === src.id)) {
            const idx = nextVideo.findIndex((s, i) => s === null && !nextLayouts[i] && !deckEntriesRef.current[i]);
            if (idx !== -1) nextVideo[idx] = src;
          }
        });

      // Marquer toutes les sources initiales comme déjà assignées
      nextVideo.forEach(s => { if (s) everAssignedToSlotRef.current.add(s.id); });

      deckSlotsRef.current       = nextVideo;
      deckSlotLayoutsRef.current = nextLayouts;
      saveDeckSlotsConfig(nextVideo, nextLayouts);
      setDeckSlots(nextVideo);
      setDeckSlotLayouts(nextLayouts);
      return;
    }

    setDeckSlots(prev => {
      const next = [...prev];
      let changed = false;

      for (let i = 0; i < next.length; i++) {
        if (next[i] && !videoSources.find(s => s.id === next[i]!.id)) {
          // La place d'un appareil encore inscrit ne se perd pas sur une absence
          // d'un rendu : on la tient, sans image, jusqu'à une décision explicite
          // (cf. releaseRemoteDeckSlot). Sinon la caméra quittait le deck à
          // l'instant même de sa reconnexion.
          if (isHeldRemoteSlot(next[i]!.id)) {
            if (!next[i]!.awaiting) {
              next[i] = { ...next[i]!, stream: undefined, awaiting: true };
              changed = true;
            }
          } else {
            next[i] = null;
            changed = true;
          }
        }
        if (next[i]) {
          const fresh = videoSources.find(s => s.id === next[i]!.id);
          if (fresh && fresh !== next[i]) next[i] = fresh;
        }
      }

      // Retour d'une source à sa place mémorisée, avant tout remplissage automatique
      if (placeRememberedSources(next)) changed = true;

      // Auto-fill uniquement pour les sources jamais assignées à un slot
      videoSources
        .filter(src => isAutoFillable(src) && !everAssignedToSlotRef.current.has(src.id))
        .forEach(src => {
          if (!next.some(s => s?.id === src.id)) {
            const idx = next.findIndex((s, i) => s === null && !deckSlotLayoutsRef.current[i] && !deckEntriesRef.current[i]);
            if (idx !== -1) {
              next[idx] = src;
              changed = true;
              everAssignedToSlotRef.current.add(src.id);
            }
          }
        });

      if (changed) saveDeckSlotsConfig(next, deckSlotLayoutsRef.current);
      deckSlotsRef.current = next;
      return next;
    });
  }, [videoSources, allLayoutSlots, isAutoFillable, isHeldRemoteSlot, deckRestoreTick]);

  useEffect(() => {
    setDeckSlotLayouts(prev => {
      let changed = false;
      const next = prev.map(layout => {
        if (!layout) return null;
        const fresh = allLayoutSlots.find(l => l.id === layout.id);
        if (!fresh) { changed = true; return null; }
        if (fresh !== layout) { changed = true; return fresh; }
        return layout;
      });
      // Une scène supprimée de la bibliothèque ne laisse pas de place réservée.
      prev.forEach((layout, i) => { if (layout && !next[i]) forgetDeckEntry(i); });
      if (!changed) return prev;
      deckSlotLayoutsRef.current = next;
      saveDeckSlotsConfig(deckSlotsRef.current, next);
      return next;
    });
  }, [allLayoutSlots]);

  useEffect(() => {
    updateVideoSources(devices.video, remoteConnections, remoteDevices, sceneVideoSources);
    // channelActive : la source « Playlist » apparaît/disparaît avec l'état de la chaîne.
    // awaitingDevices : une place tenue est une source de plus dans la liste.
  }, [remoteDevices, remoteConnections, devices.video, externalSources, sceneVideoSources, screenShareSlots, screenShareStreams, channelActive, awaitingDevices, audioOnlySources]);

  // ── Init preview + flux final sur la première caméra ─────────────────────
  useEffect(() => {
    if (!videoSources.length) return;
    if (deckPreviewInitRef.current) return;

    const firstDevice = videoSources.find(s => s.type === 'device');
    if (!firstDevice) return;
    deckPreviewInitRef.current = true;

    deckPreviewSourceIdRef.current = firstDevice.id;
    setDeckPreviewSourceId(firstDevice.id);
    startPreviewForSource(firstDevice).then(() => {
      const stream = previewStreamsRef.current.get(firstDevice.id);
      if (stream && deckPreviewVideoRef.current) {
        deckPreviewVideoRef.current.srcObject = stream;
        deckPreviewVideoRef.current.play().catch(() => {});
      }
    });

    if (!selectedVideoSource) {
      setSelectedVideoSource(firstDevice.id);
      onHandleDeckSwitch(firstDevice);
    }
  }, [videoSources]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      // Une régie qui ne pilote pas est voilée à la souris (cf. LiveStreamManager),
      // mais un voile n'arrête pas le clavier : sans ce garde, les raccourcis du
      // pupitre — passage à l'antenne, cut, images, sons, PiP, jingles — restaient
      // le seul chemin ouvert pour agir depuis une spectatrice. On lit le ref, pas
      // l'état : cet effet est monté une fois pour toutes, à dépendances vides.
      if (!controlStateRef.current.isHolder) return;
      const km   = keyMapRef.current;
      const { serverUrl } = settingsRef.current;
      const streamPath = effectiveStreamPathRef.current;
      if (!streamPath) return;
      const base = serverUrl.replace(/\/$/, '');
      for (let i = 0; i < km.video.length; i++) {
        const kb = km.video[i];
        if (!kb || !bindingMatches(kb, e)) continue;
        e.preventDefault();
        const row = videoRowRef.current[i];
        if (!row) return;
        if (row.slot) { onHandleDeckSwitchRef.current(row.slot); }
        else if (row.isHlsVideo) { onHandleDeckSwitchRef.current({ id: `hls-video-${streamPath}`, label: 'Playlist', type: 'url', externalUrl: `${base}/video_${streamPath}/index.m3u8` }); }
        else if (row.isHlsLive)  { onHandleDeckSwitchRef.current({ id: `hls-live-${streamPath}`,  label: 'OBS, RTMP...',  type: 'url', externalUrl: `${base}/live_${streamPath}/index.m3u8`  }); }
        return;
      }
      for (let i = 0; i < (km.preview ?? []).length; i++) {
        const kb = (km.preview ?? [])[i];
        if (!kb || !bindingMatches(kb, e)) continue;
        e.preventDefault();
        const row = videoRowRef.current[i];
        if (!row) return;
        let src: VideoSource | null = row.slot ?? null;
        if (!src) {
          if (row.isHlsVideo) src = { id: `hls-video-${streamPath}`, label: 'Playlist', type: 'url', externalUrl: `${base}/video_${streamPath}/index.m3u8` };
          else if (row.isHlsLive) src = { id: `hls-live-${streamPath}`, label: 'OBS, RTMP...', type: 'url', externalUrl: `${base}/live_${streamPath}/index.m3u8` };
        }
        if (src) handleDeckPreviewRef.current(src);
        return;
      }
      const cutKb = (km.cut ?? [])[0];
      if (cutKb && bindingMatches(cutKb, e)) { e.preventDefault(); onHandleDeckCutRef.current(); return; }
      for (let i = 0; i < km.image.length; i++) { const kb = km.image[i]; if (kb && bindingMatches(kb, e)) { e.preventDefault(); const slot = imageSlotsRef.current[i]; if (slot?.url) onToggleImageSlot(slot.id); return; } }
      for (let i = 0; i < km.sound.length; i++) { const kb = km.sound[i]; if (kb && bindingMatches(kb, e)) { e.preventDefault(); const slot = soundSlotsRef.current[i]; if (slot?.url) onTriggerSoundSlot(slot); return; } }
      // PiP : raccourcis désactivés (plus de bouton dans le deck, aucun éditeur d'emplacement).
      for (let i = 0; i < (km.jingle ?? []).length; i++) { const kb = (km.jingle ?? [])[i]; if (kb && bindingMatches(kb, e)) { e.preventDefault(); const slot = jingleSlotsRef.current[i]; if (slot?.url) onTriggerJingleSlot(slot); return; } }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * La signalisation suit la chaîne — et rien d'autre.
   *
   * Cet effet dépendait auparavant du seul `channelActive`, et la connexion
   * initiale se faisait dans l'effet de montage à dépendances vides : la socket
   * gardait donc pour toujours le chemin qu'elle avait au montage, où l'UUID de la
   * chaîne n'est pas encore résolu. Elle se rabattait alors sur l'UUID en dur de
   * DEFAULT_SETTINGS, et son propre `onclose` la ressuscitait toutes les 3 s : la
   * régie tenait deux canaux de contrôle, celui de sa chaîne et un parasite sur
   * une autre, aussi longtemps que l'onglet restait ouvert.
   *
   * On ferme donc explicitement avant de rouvrir dès que la chaîne change.
   */
  useEffect(() => {
    disconnectSignaling();

    if (!channelActive || !deviceStreamPath || observerMode === 'pending') { return; }

    const cleanupSignaling = connectToSignalingServer();

    return () => {
      disconnectSignaling();
      cleanupSignaling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelActive, deviceStreamPath, observerMode]);

  useEffect(() => {
    loadDevices();
    getLocalDevices();
    navigator.mediaDevices.addEventListener('devicechange', getLocalDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getLocalDevices);
      // La signalisation a son propre effet, qui se ferme tout seul.
      if (whipStatsIntervalRef.current) clearInterval(whipStatsIntervalRef.current);
      // Webcam et écran ÉPARGNÉS, comme releaseChannelScopedSources (SourceRegistryContext) :
      // le registre vit à la racine et survit au changement de chaîne, mais CE emprunt-ci
      // (mini-vue) était le seul restant sur la webcam si elle n'était pas aussi à l'antenne
      // — le relâcher ici faisait tomber son refCount à 0 et coupait la piste (track.stop())
      // au moment même de quitter la chaîne, malgré l'intention du 2026-08-28. Trouvé le
      // 15/09/2026 : webcam visible en mini-vue sur ESSAI, plus d'image sur Lovelight TV
      // ensuite (piste renvoyée `readyState=ended` par le flux réemprunté).
      previewBorrowHandlesRef.current.forEach((handle, sourceId) => {
        const type = registry.get(sourceId)?.type;
        if (type && PERSISTENT_SOURCE_TYPES.includes(type)) return;
        try { handle.release(); } catch {}
        previewBorrowHandlesRef.current.delete(sourceId);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-borrow pour les mini-vues quand une source devient active ──────
  // v2.44 : ignore les sources dans poweredOffSlots pour éviter le re-borrow
  // immédiat après un power OFF.
  useEffect(() => {
    deckSlotsRef.current.forEach(slot => {
      if (!slot) return;
      if (poweredOffSlots.has(slot.id)) return;        // ← AJOUT v2.44
      if (slot.type === 'audio') return;               // audio seul : pas d'image à emprunter
      if (previewBorrowHandlesRef.current.has(slot.id)) return;
      if (previewStreamsRef.current.get(slot.id)?.active) return;

      // Une source en erreur ne se réacquiert pas toute seule : la panne est
      // souvent durable (caméra partie, permission révoquée) et chaque échec
      // d'acquisition réémet, ce qui relancerait cet effet en boucle serrée.
      // C'est l'opérateur qui relance, par le bouton de la fenêtre de réglage.
      if (registry.getState(slot.id) === 'error') return;

      // Un flux distant — HLS, Xtream, caméra RTMP — n'est PAS acquis pour sa
      // seule mini-vue : ouvrir un lecteur par emplacement au motif qu'une URL y
      // est posée ferait tirer huit flux à l'ouverture de la régie, dont ceux qui
      // ne publient pas encore.
      //
      // SAUF s'il est déjà acquis, c'est-à-dire à l'antenne ou en preview. Le
      // stream existe alors, le registre le partage par compte de références, et
      // l'emprunt de la mini-vue ne coûte pas un lecteur de plus. Sans ce cas, une
      // caméra RTMP envoyée à l'antenne s'y voyait sans jamais entrer dans la
      // mosaïque : il fallait la passer en preview pour l'y faire apparaître.
      if (slot.type === 'url' && registry.getState(slot.id) !== 'active') return;

      if (slot.type === 'screen') {
        const regState = registry.getState(slot.id);
        if (regState !== 'active') return;
      }

      registry.borrow(slot.id).then(handle => {
        previewBorrowHandlesRef.current.set(slot.id, handle);
        previewStreamsRef.current.set(slot.id, handle.stream);
        setPreviewStreams(new Map(previewStreamsRef.current));
      }).catch(() => {});
    });
  }, [registrySources, deckSlots, registry, poweredOffSlots]);  // ← poweredOffSlots ajouté

  // ── Une source qui meurt en cours de route ────────────────────────────────
  //
  // Le registre émet `error` quand une acquisition est perdue : piste caméra
  // arrêtée par le navigateur, périphérique débranché, réacquisition échouée. Le
  // stream emprunté est alors mort mais toujours branché sur la mini-vue, qui reste
  // noire — un emplacement pourvu, sans image et sans explication. On le débranche
  // pour que la case retombe à l'état « vide », qui lui est lisible.
  //
  // L'emprunt lui-même n'est PAS relâché : `release` ferait tomber refCount à zéro,
  // donc `doRelease`, qui repasse la source à `idle` — et l'auto-borrow ci-dessus la
  // réacquerrait aussitôt. Le handle conservé est justement ce qui tient la boucle
  // fermée jusqu'au clic de l'opérateur, qui le libère par `handleReloadSource`.
  useEffect(() => registry.on('error', event => {
    if (!previewStreamsRef.current.has(event.sourceId)) return;
    previewStreamsRef.current.delete(event.sourceId);
    setPreviewStreams(new Map(previewStreamsRef.current));
  }), [registry, previewStreamsRef, setPreviewStreams]);

  // ── Un flux remplacé sous une mini-vue déjà pourvue ───────────────────────
  //
  // Le registre remplace le flux d'une source sans prévenir personne d'autre que
  // le flux final (cf. l'abonnement `stream-updated` de LiveStreamManager, qui ne
  // recompose que la source à l'antenne). La mini-vue, elle, affiche le
  // MediaStream qu'elle a emprunté une fois pour toutes : après une reconnexion
  // de téléphone, elle continue de montrer l'ancien.
  //
  // Et rien ne la sort de là. Un flux WebRTC distant orphelin garde des pistes
  // `live` et un `.active` à true — c'est le piège documenté dans
  // api_webrtcPeerConnection : `startPreviewForSource` le croit vivant et renonce,
  // l'auto-emprunt plus haut voit un handle en place et renonce aussi. D'où la
  // vignette noire d'une caméra qu'on voit pourtant très bien en la passant au
  // flux final. C'était le défaut du 2026-08-31, sur le retour d'une coupure qui
  // ne passe par aucun `device_disconnected` (échec WHEP transitoire).
  //
  // Sauf pour une source posée sur le deck dont la PREMIÈRE acquisition avait
  // raté : l'auto-emprunt (effet ci-dessus) ne rejoue que si `registrySources`/
  // `deckSlots` changent à nouveau, ce qui n'arrive plus une fois le device
  // stabilisé. Un device SFU lent à publier (device_connected reçu bien avant
  // que le WHEP aboutisse) rate ainsi la seule fenêtre de retentative et reste
  // noir indéfiniment — vu sur un iPhone le 2026-09-04, `injectStream` arrivé
  // ~10 min après la pose sur le deck, jamais rattrapé automatiquement. On
  // rattrape ici, sur ce même événement qui dit justement « le flux est prêt
  // maintenant » : borner à une source posée sur le deck et non éteinte évite
  // de retomber dans le « huit flux à l'ouverture » que l'auto-emprunt évite.
  //
  // L'ordre compte : emprunter AVANT de relâcher. L'inverse ferait tomber le
  // compte de références à zéro entre les deux, donc `doRelease` — la source
  // repasserait `idle` et le flux qu'on vient d'injecter serait démonté.
  useEffect(() => registry.on('stream-updated', event => {
    const nextStream = event.source.stream;
    if (!nextStream) return;

    const shownStream = previewStreamsRef.current.get(event.sourceId);
    const heldHandle  = previewBorrowHandlesRef.current.get(event.sourceId);

    if (!shownStream && !heldHandle) {
      if (poweredOffSlots.has(event.sourceId)) return;
      if (!deckSlotsRef.current.some(s => s?.id === event.sourceId)) return;

      registry.borrow(event.sourceId).then(handle => {
        previewBorrowHandlesRef.current.set(event.sourceId, handle);
        previewStreamsRef.current.set(event.sourceId, handle.stream);
        setPreviewStreams(new Map(previewStreamsRef.current));
        console.warn(`[MINIVUE] ▶️ ${event.sourceId} — première acquisition rattrapée après injection tardive`);
      }).catch(() => {});
      return;
    }

    if (shownStream === nextStream) return;

    registry.borrow(event.sourceId).then(handle => {
      previewBorrowHandlesRef.current.set(event.sourceId, handle);
      previewStreamsRef.current.set(event.sourceId, handle.stream);
      setPreviewStreams(new Map(previewStreamsRef.current));
      if (heldHandle) { try { heldHandle.release(); } catch {} }
      console.warn(`[MINIVUE] 🔄 ${event.sourceId} — flux remplacé, mini-vue réempruntée`);
    }).catch(() => {
      // L'emprunt a échoué : mieux vaut une case vide, qui se lit, qu'une image
      // figée d'un flux mort. L'auto-emprunt reprendra la main.
      if (heldHandle) {
        try { heldHandle.release(); } catch {}
        previewBorrowHandlesRef.current.delete(event.sourceId);
      }
      previewStreamsRef.current.delete(event.sourceId);
      setPreviewStreams(new Map(previewStreamsRef.current));
    });
  }), [registry, previewStreamsRef, previewBorrowHandlesRef, setPreviewStreams, poweredOffSlots, deckSlotsRef]);




  return {
    controlState, requestControl,
    controlRequest, forceControl, cancelControlRequest,
    incomingControlRequest, answerControlRequest,
    devices, remoteDevices, codecProbeState,
    previewStreams, setPreviewStreams, previewStreamsRef,
    deckPreviewSourceId, setDeckPreviewSourceId,
    deckPreviewSourceIdRef,
    deckPreviewVideoRef,
    deckSlots, deckSlotLayouts,
    keyMap, handleKeyMapChange,
    handleDeckSlotChange,
    updateAudioOnlySource,
    handleDeckSlotsSwap,
    reserveDeckSlot,
    renameDeckSlotSource,
    handleDeckSlotLayoutChange,
    loadDevices, handleRegisterDevice, handleDeleteDevice, handleDisconnectDevice, handleRegeneratePassword,
    handleSetDirector, directorDevices, directorDevicesRef, sendDirectorDeck,
    deviceNetworkQuality,
    deviceGeo,
    setDeviceGeoWatch,
    setDeviceMetricsLog,
    handleDeckPreview, startPreviewForSource, stopPreviewForSource,
    acquireExternalUrlStream,
    remoteDeviceInfoRef, remoteConnectionsRef, signalingWsRef, requiredCodecRef,
    whipOfferSdpRef, whipAnswerSdpRef,
    buildSignalingRefs,
    buildSignalingCallbacks,
    resetDeckInit,
    resetDeckToDefaults,
    remapSourceIds,
    markSourcesKnown,
    deleteDeviceNow,
    previewBorrowHandlesRef,   // ← AJOUT v2.44
    forgetSlotAssignment,
    releaseRemoteDeckSlot,
    markDeviceAwaiting,
    clearDeviceAwaiting,
    renewDeviceAwaiting,
    awaitingDevices,
    handleSignalingMessageRef, // ← AJOUT v2.45
    remoteDeviceTeardownRef,   // ← AJOUT v2.46
  };
}